// bluaIQ — P3: Drawing Markup Module
// SVG-based drawing overlay on uploaded plan images with 7 tools

import { apiFetch } from '../utils/api'
import { actorLabelText, type ActorLabel } from '../shared/actorLabel'
import { formatPlanUploadSuccess } from './planUploadSuccessCopy'
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  PenTool, MousePointer, Minus, Square, Circle, Type, ArrowRight,
  Palette, Layers, Save, Trash2, Eye, EyeOff, Plus, Download,
  ChevronLeft, ChevronRight, FileText, Upload, Loader2,
  Lock, Unlock, Pencil, Check, X
} from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import { useTakeoffAvailable } from '../takeoff-diagnostic-v3/TakeoffAvailabilityContext'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'
import { composeSheetPngDataUrl, composeSheetPngFromElements, buildImagesPdfBlob, downloadDataUrl, downloadPdfBlobSmart, printImageDataUrls, sanitizeFilename } from '../utils/sheetExport'

// ── TYPES ─────────────────────────────────────────

type ToolType = 'select' | 'pen' | 'line' | 'rectangle' | 'circle' | 'text' | 'arrow'

interface MarkupSheet {
  id: string
  name: string
  nameEs: string
  imageUrl?: string
  // Optional per-sheet provenance (stored in the sheets JSON — no schema). Lets
  // the UI show which uploaded file/date a sheet came from.
  sourceFile?: string
  uploadedAt?: string
  // Plan Sets (PR-PM2): association to a DrawingPlanSet. Populated by the server
  // backfill; not yet surfaced in the UI (PR-PM3+).
  setId?: string
}

// The markup canvas is a fixed-viewBox SVG. Pointer coordinates must be mapped
// INTO this user space or shapes land offset from the cursor (desktop bug).
const VIEWBOX_W = 700
const VIEWBOX_H = 420

// Plan Sets (PR-PM3) — a DrawingPlanSet row as returned by the API. The server
// resolves the uploader server-side and sends a client-safe `uploadedByLabel`;
// the raw Clerk id in `uploadedBy` is NOT on the wire anymore (never was safe to
// render). See src/shared/actorLabel.ts.
interface PlanSet {
  id: string
  name: string
  sourceFile?: string | null
  revisionLabel?: string | null
  status?: string // CURRENT | ARCHIVED | SUPERSEDED | DRAFT
  uploadedByLabel?: ActorLabel | null
  sheetCount?: number
  createdAt?: string
  updatedAt?: string
}

// A file queued in the pending-upload batch (before the user confirms).
interface PendingPlanFile {
  file: File
  pages: number | null // estimated page/sheet count (null = unknown/pending)
}

function buildProtectedUploadUrl(storagePath?: string, token?: string | null): string {
  if (!storagePath) return ''
  if (/^https?:\/\//i.test(storagePath)) return storagePath
  if (token && storagePath.startsWith('/api/uploads/')) {
    return `${storagePath}?token=${encodeURIComponent(token)}`
  }
  return storagePath
}

interface MarkupElement {
  id: string
  type: Exclude<ToolType, 'select'>
  points?: { x: number; y: number }[]
  x?: number
  y?: number
  width?: number
  height?: number
  text?: string
  color: string
  strokeWidth: number
  layerId: string
  sheetId: string
}

interface MarkupLayer {
  id: string
  name: string
  nameEs: string
  visible: boolean
  color: string
  // PR-PM7: lock a layer so its markups can't be selected/edited/deleted and no
  // new markups can be drawn into it while active. Optional → back-compat with
  // existing JSON (absent = unlocked). Persisted in DrawingMarkup JSON, no schema.
  locked?: boolean
}

interface MarkupData {
  id: string
  projectId: string
  elements: MarkupElement[]
  layers: MarkupLayer[]
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_SHEETS: MarkupSheet[] = [
  { id: 'sheet-1', name: 'A1 — Floor Plan', nameEs: 'A1 — Planta' },
  { id: 'sheet-2', name: 'A2 — Elevations', nameEs: 'A2 — Elevaciones' },
  { id: 'sheet-3', name: 'E1 — Electrical', nameEs: 'E1 — Eléctrico' },
  { id: 'sheet-4', name: 'P1 — Plumbing', nameEs: 'P1 — Plomería' },
]

const DEMO_LAYERS: MarkupLayer[] = [
  { id: 'layer-plumbing', name: 'Plumbing', nameEs: 'Plomería', visible: true, color: '#3B82F6' },
  { id: 'layer-electrical', name: 'Electrical', nameEs: 'Eléctrico', visible: true, color: '#F59E0B' },
  { id: 'layer-structural', name: 'Structural Notes', nameEs: 'Notas Estructurales', visible: true, color: '#EF4444' },
]

// PR-NEW: real projects start with a single generic "Notes"
// layer (deterministic id) so the SVG canvas has somewhere to
// draw once the user uploads a plan. Trade-specific layers are
// added on demand via the existing newLayer flow further down
// this file. The DEMO_LAYERS array stays only for the
// `isDemoProject` demo path.
const DEFAULT_REAL_LAYERS: MarkupLayer[] = [
  { id: 'layer-notes-default', name: 'Notes', nameEs: 'Notas', visible: true, color: '#3B82F6' },
]

const DEMO_ELEMENTS: MarkupElement[] = [
  { id: 'el-1', type: 'rectangle', x: 120, y: 80, width: 160, height: 100, color: '#3B82F6', strokeWidth: 2, layerId: 'layer-plumbing', sheetId: 'sheet-1' },
  { id: 'el-2', type: 'line', points: [{ x: 200, y: 130 }, { x: 400, y: 130 }], color: '#3B82F6', strokeWidth: 2, layerId: 'layer-plumbing', sheetId: 'sheet-1' },
  { id: 'el-3', type: 'circle', x: 450, y: 200, width: 60, height: 60, color: '#F59E0B', strokeWidth: 2, layerId: 'layer-electrical', sheetId: 'sheet-3' },
  { id: 'el-4', type: 'arrow', points: [{ x: 450, y: 200 }, { x: 550, y: 120 }], color: '#F59E0B', strokeWidth: 2, layerId: 'layer-electrical', sheetId: 'sheet-3' },
  { id: 'el-5', type: 'text', x: 560, y: 115, text: 'Panel Location', color: '#F59E0B', strokeWidth: 1, layerId: 'layer-electrical', sheetId: 'sheet-3' },
  { id: 'el-6', type: 'pen', points: [{ x: 100, y: 300 }, { x: 130, y: 280 }, { x: 160, y: 310 }, { x: 200, y: 290 }], color: '#EF4444', strokeWidth: 3, layerId: 'layer-structural', sheetId: 'sheet-2' },
  { id: 'el-7', type: 'text', x: 100, y: 270, text: 'Reinforce beam', color: '#EF4444', strokeWidth: 1, layerId: 'layer-structural', sheetId: 'sheet-2' },
]

const COLORS = ['#EF4444', '#F59E0B', '#22C55E', '#3B82F6', '#00BCD4', '#EC4899', '#1C1A17']
const STROKE_WIDTHS = [1, 2, 3, 5]

const TOOLS: { type: ToolType; icon: typeof MousePointer }[] = [
  { type: 'select', icon: MousePointer },
  { type: 'pen', icon: PenTool },
  { type: 'line', icon: Minus },
  { type: 'rectangle', icon: Square },
  { type: 'circle', icon: Circle },
  { type: 'text', icon: Type },
  { type: 'arrow', icon: ArrowRight },
]

// ── COMPONENT ─────────────────────────────────────

export default function DrawingMarkup() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  const queryClient = useQueryClient()
  const planFileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState<string | null>(null)
  // Plan Sets (PR-PM3) — multi-file pending batch + set decision flow.
  // pendingBatch: files queued for review/removal before upload.
  // batchStep: which modal step is showing (null = none).
  // replaceTargetSetId: set chosen for "Replace specific set".
  // activeSetId: the plan set currently shown/edited on the canvas.
  const [pendingBatch, setPendingBatch] = useState<PendingPlanFile[]>([])
  const [batchStep, setBatchStep] = useState<'list' | 'pick-replace' | 'warn-replace' | null>(null)
  const [replaceTargetSetId, setReplaceTargetSetId] = useState<string | null>(null)
  const [activeSetId, setActiveSetId] = useState<string | null>(null)
  // Plan-set lifecycle confirmation (PR-PM5): archive / restore / supersede.
  const [pendingSetAction, setPendingSetAction] = useState<{ setId: string; setName: string; action: 'archive' | 'restore' | 'supersede' } | null>(null)
  const [settingSetStatus, setSettingSetStatus] = useState(false)
  // PR-PM6: coarse-pointer (touch) detection. Drawing markups uses mouse handlers
  // only; on touch devices we show an honest note instead of pretending touch
  // drawing works. Viewing/upload/lifecycle all work on touch.
  const [isCoarsePointer, setIsCoarsePointer] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(pointer: coarse)')
    const update = () => setIsCoarsePointer(mq.matches)
    update()
    // PR-PM7 mobile: the layers panel becomes a bottom sheet on touch — start it
    // CLOSED so it never covers the canvas; the header Layers button opens it.
    if (mq.matches) setShowLayers(false)
    mq.addEventListener?.('change', update)
    return () => mq.removeEventListener?.('change', update)
  }, [])
  const [uploadToken, setUploadToken] = useState<string | null>(null)
  // Sheet image load tracking. A Clerk token in the image URL expires (~60s), and
  // on first paint the token is still null (async) — so a naive onError would mark
  // a perfectly good sheet "failed" forever. We therefore retry once with a fresh
  // token (+ cache-buster) before declaring a real failure, and only treat a sheet
  // as drawable once its image has actually loaded.
  const [failedSheets, setFailedSheets] = useState<Set<string>>(new Set())
  const [loadedSheets, setLoadedSheets] = useState<Set<string>>(new Set())
  const [imageReloadNonce, setImageReloadNonce] = useState(0)
  const retriedSheets = useRef<Set<string>>(new Set())
  // Export menu (PR-PM-1): the toolbar Export button opens this; it was a no-op.
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [exportWarn, setExportWarn] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [hydrated, setHydrated] = useState(false)
  const [showTakeoffFiles, setShowTakeoffFiles] = useState(false)

  // PR-NEW: only the explicit demo project initializes with
  // DEMO_SHEETS / DEMO_LAYERS / DEMO_ELEMENTS. Real projects
  // start with empty sheets + empty elements + a single generic
  // "Notes" layer; the empty-state UI below tells the user how
  // to upload plans. The hydration useEffect below replaces
  // these defaults the moment apiMarkups arrives with content.
  const isDemo = isDemoProject(projectId)
  const initialSheets = isDemo ? DEMO_SHEETS : []
  const initialLayers = isDemo ? DEMO_LAYERS : DEFAULT_REAL_LAYERS
  const initialElements = isDemo ? DEMO_ELEMENTS : []
  const initialActiveLayerId = isDemo ? 'layer-plumbing' : 'layer-notes-default'

  const [activeTool, setActiveTool] = useState<ToolType>('select')
  const [activeColor, setActiveColor] = useState('#3B82F6')
  const [activeStroke, setActiveStroke] = useState(2)
  const [activeLayerId, setActiveLayerId] = useState(initialActiveLayerId)
  const [sheets, setSheets] = useState<MarkupSheet[]>(initialSheets)
  const [activeSheetIdx, setActiveSheetIdx] = useState(0)
  const [layers, setLayers] = useState<MarkupLayer[]>(initialLayers)
  const [elements, setElements] = useState<MarkupElement[]>(initialElements)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showLayers, setShowLayers] = useState(true)
  // PR-PM7 layer management: inline rename, safe delete (move/purge markups),
  // last-layer protection, lock.
  const [renamingLayerId, setRenamingLayerId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [layerError, setLayerError] = useState<string | null>(null)
  const [pendingLayerDelete, setPendingLayerDelete] = useState<{ layerId: string; name: string; count: number } | null>(null)
  const [deleteChoice, setDeleteChoice] = useState<'move' | 'purge'>('move')
  const [moveTargetId, setMoveTargetId] = useState('')
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null)
  const [penPoints, setPenPoints] = useState<{ x: number; y: number }[]>([])
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    let alive = true
    getToken()
      .then((token) => { if (alive) setUploadToken(token || null) })
      .catch(() => { if (alive) setUploadToken(null) })
    return () => { alive = false }
  }, [getToken])

  // Fetch a fresh Clerk token for image URLs (they expire ~60s). Returns it AND
  // updates state so subsequent <image> renders use it.
  const refreshUploadToken = async (): Promise<string | null> => {
    try {
      const fresh = await getToken()
      setUploadToken(fresh || null)
      return fresh || null
    } catch {
      setUploadToken(null)
      return null
    }
  }

  // <image> failed to load. The usual cause is an expired/missing token in the
  // URL (not a genuinely broken file), so retry ONCE with a fresh token and a
  // cache-buster before showing the render-error. retriedSheets caps it at one
  // refresh per sheet → no loops.
  const handleSheetImageError = (sheetId: string) => {
    if (retriedSheets.current.has(sheetId)) {
      setFailedSheets(prev => { const next = new Set(prev); next.add(sheetId); return next })
      return
    }
    retriedSheets.current.add(sheetId)
    refreshUploadToken()
      .then(fresh => {
        if (fresh) setImageReloadNonce(n => n + 1) // force reload with the fresh token
        else setFailedSheets(prev => { const next = new Set(prev); next.add(sheetId); return next })
      })
  }

  const handleSheetImageLoad = (sheetId: string) => {
    setLoadedSheets(prev => { const next = new Set(prev); next.add(sheetId); return next })
    setFailedSheets(prev => { if (!prev.has(sheetId)) return prev; const next = new Set(prev); next.delete(sheetId); return next })
  }

  const { data: apiMarkups } = useQuery<MarkupData & { sheets?: MarkupSheet[] }>({
    queryKey: ['markups', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/drawing-markups`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  // Cuantificación switch: "Importar de Takeoff" only while takeoff is on.
  const takeoffAvailable = useTakeoffAvailable()
  const { data: takeoffFiles } = useQuery<{ id: string; originalFilename: string; storagePath: string; mimeType: string; revisionLabel?: string; createdAt: string }[]>({
    queryKey: ['file-versions', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/file-versions?fileType=PLANO_ARQUITECTONICO`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    enabled: !!projectId,
  })

  // Upload a plan file. PDFs are rasterized in the BROWSER (native DOMMatrix +
  // canvas) and their page PNGs are sent to the canvas-free upload-pages route;
  // images upload directly. This bypasses the server-side @napi-rs/canvas native
  // addon that fails to load on Railway. Returns the server payload (with
  // sheets) or throws { code } so callers show the specific bilingual message.
  const uploadPlanData = async (
    data: Blob,
    filename: string,
    opts: { setMode: 'new' | 'add' | 'replace'; targetSetId?: string | null },
  ): Promise<{ sheets?: MarkupSheet[]; added?: number; setId?: string; planSets?: PlanSet[] }> => {
    const token = await getToken()
    const isPdf = (data.type || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(filename)
    const stampForm = (fd: FormData) => {
      fd.append('setMode', opts.setMode)
      if (opts.targetSetId) fd.append('setId', opts.targetSetId)
      fd.append('filename', filename)
    }
    if (isPdf) {
      let pageBlobs: Blob[]
      try {
        const { renderPdfToPngBlobs } = await import('../utils/pdfRenderClient')
        pageBlobs = await renderPdfToPngBlobs(await data.arrayBuffer())
      } catch (e) {
        console.error('Client PDF render error:', e)
        throw { code: 'PDF_PROCESSING' }
      }
      if (pageBlobs.length === 0) throw { code: 'PDF_PROCESSING' }
      const formData = new FormData()
      pageBlobs.forEach((b, i) => formData.append('pages', b, `page-${i + 1}.png`))
      stampForm(formData)
      const res = await apiFetch(`/api/projects/${projectId}/drawing-markups/upload-pages`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw { code: err?.code, error: err?.error, status: res.status }
      }
      return res.json()
    }
    // Image → canvas-free single-file route.
    const formData = new FormData()
    formData.append('file', data, filename)
    stampForm(formData)
    const res = await apiFetch(`/api/projects/${projectId}/drawing-markups/upload`, {
      method: 'POST',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw { code: err?.code, error: err?.error, status: res.status }
    }
    return res.json()
  }

  // Estimate page/sheet count per pending file (PDFs via pdfjs, images = 1).
  const estimateBatchPages = async (items: PendingPlanFile[]) => {
    for (const item of items) {
      const isPdf = (item.file.type || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(item.file.name)
      let pages: number | null = 1
      if (isPdf) {
        try {
          const { countPdfPages } = await import('../utils/pdfRenderClient')
          pages = await countPdfPages(await item.file.arrayBuffer())
        } catch { pages = null }
      }
      setPendingBatch(prev => prev.map(p => (p.file === item.file ? { ...p, pages } : p)))
    }
  }

  // File input / takeoff import → build the pending batch (review + remove before
  // upload). Never uploads silently: the user reviews, then chooses set behavior.
  const requestBatchUpload = (files: File[]) => {
    setUploadSuccessMsg(null); setUploadError(null)
    const valid = files.filter(f => isSupportedPlan(f.type, f.name))
    const skipped = files.length - valid.length
    if (valid.length === 0) {
      setUploadError(planUploadErrorMessage('UNSUPPORTED'))
      if (planFileRef.current) planFileRef.current.value = ''
      return
    }
    if (skipped > 0) setUploadError(isEs ? `${skipped} archivo(s) no compatibles omitidos` : `${skipped} unsupported file(s) skipped`)
    const items: PendingPlanFile[] = valid.map(f => ({ file: f, pages: null }))
    setPendingBatch(items)
    setBatchStep('list')
    void estimateBatchPages(items)
    if (planFileRef.current) planFileRef.current.value = ''
  }

  const removePendingFile = (idx: number) => setPendingBatch(prev => prev.filter((_, i) => i !== idx))

  const cancelBatch = () => {
    setPendingBatch([]); setBatchStep(null); setReplaceTargetSetId(null)
    if (planFileRef.current) planFileRef.current.value = ''
  }

  // Run the batch through the chosen set decision. 'new' → each file its own set;
  // 'add' → all into the target set; 'replace' → first file replaces the target
  // set, the rest add to it. Markups are never deleted (replace disconnects only).
  const runBatchUpload = async (decision: 'new' | 'add' | 'replace', targetSetId?: string | null) => {
    const batch = pendingBatch
    if (batch.length === 0) return
    setBatchStep(null); setPendingBatch([]); setReplaceTargetSetId(null)
    setUploadError(null); setUploadSuccessMsg(null)
    setUploading(true)
    let last: { sheets?: MarkupSheet[]; setId?: string; planSets?: PlanSet[]; added?: number } | null = null
    let failed = 0, createdSets = 0, totalSheets = 0
    try {
      for (let i = 0; i < batch.length; i++) {
        const f = batch[i].file
        const setMode: 'new' | 'add' | 'replace' =
          decision === 'new' ? 'new' : decision === 'add' ? 'add' : (i === 0 ? 'replace' : 'add')
        try {
          const data = await uploadPlanData(f, f.name, {
            setMode,
            targetSetId: setMode === 'new' ? null : (last?.setId ?? targetSetId ?? null),
          })
          last = data
          if (setMode === 'new') createdSets++
          // Accumulate the TRUE total: each upload reports the sheets it ADDED,
          // so the sum is correct across multi-PDF / multi-set batches (Codex #471).
          totalSheets += (data as { added?: number }).added ?? 0
        } catch (err: any) {
          console.error('Plan batch upload error:', err?.error || err?.code || err)
          failed++
        }
      }
      // Refresh from the last successful response with a fresh token.
      await refreshUploadToken()
      retriedSheets.current = new Set()
      setFailedSheets(new Set()); setLoadedSheets(new Set())
      if (last?.sheets) {
        setSheets(last.sheets)
        if (last.setId) setActiveSetId(last.setId)
        setActiveSheetIdx(0)
      }
      // Seed the query cache with the response so the plan-set list + file-info
      // reflect updated metadata (e.g. the replaced set's new name) IMMEDIATELY,
      // not only after the refetch below.
      if (last?.planSets) {
        queryClient.setQueryData(['markups', projectId], (old: any) =>
          old ? { ...old, sheets: last!.sheets ?? old.sheets, planSets: last!.planSets } : old)
      }
      queryClient.invalidateQueries({ queryKey: ['markups', projectId] })
      const ok = batch.length - failed
      if (ok === 0) {
        setUploadError(planUploadErrorMessage(undefined))
      } else {
        // Bug 1 (Codex #471): the count is the TRUE total across the batch
        // (totalSheets, summed from each upload's `added`), not the last set's
        // length — two PDFs of 3 and 5 sheets now read "8 hojas en total", with
        // real pluralization (1 hoja / N hojas · 1 set / N sets), ES + EN.
        setUploadSuccessMsg(
          formatPlanUploadSuccess(
            { decision, createdSets, filesOk: ok, totalSheets, currentSetSheets: last?.sheets?.length ?? 0, failed },
            isEs,
          ),
        )
        setTimeout(() => setUploadSuccessMsg(null), 4000)
      }
    } finally {
      setUploading(false)
      if (planFileRef.current) planFileRef.current.value = ''
    }
  }

  const handleImportTakeoffFile = async (file: { storagePath: string; originalFilename: string; mimeType: string }) => {
    setUploadError(null)
    setShowTakeoffFiles(false)
    try {
      const token = await getToken()
      const fileRes = await apiFetch(`/api/uploads/${file.storagePath}`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } })
      if (!fileRes.ok) throw { code: undefined }
      const blob = await fileRes.blob()
      // Goes through the same pending-batch + set-decision flow as a manual upload.
      requestBatchUpload([new File([blob], file.originalFilename, { type: blob.type })])
    } catch (err: any) {
      console.error('Plan import error:', err?.error || err?.code || err)
      setUploadError(planUploadErrorMessage(err?.code))
    }
  }

  // Hydrate elements/layers/sheets from the API the first time data arrives.
  // Without this the Save button is a one-way street: data persists but never loads.
  useEffect(() => {
    if (hydrated || !apiMarkups) return
    if (Array.isArray(apiMarkups.sheets) && apiMarkups.sheets.length > 0) {
      setSheets(apiMarkups.sheets)
    }
    if (Array.isArray(apiMarkups.elements) && apiMarkups.elements.length > 0) {
      setElements(apiMarkups.elements)
    }
    if (Array.isArray(apiMarkups.layers) && apiMarkups.layers.length > 0) {
      setLayers(apiMarkups.layers)
    }
    setHydrated(true)
  }, [apiMarkups, hydrated])

  // Single source of truth = local `sheets` state. It is hydrated from the API on
  // load (effect above) and updated on every upload/add/replace. The API copy is
  // only a fallback for the brief pre-hydration window. Preferring the API copy
  // first showed the STALE cached set right after an Add/Replace (wrong sheet /
  // placeholder) until react-query refetched.
  const apiSheets = Array.isArray(apiMarkups?.sheets) ? apiMarkups.sheets : []
  const allSheets: MarkupSheet[] = sheets.length > 0 ? sheets : apiSheets

  // Plan Sets (PR-PM3): the API returns the project's DrawingPlanSet rows.
  const planSets: PlanSet[] = Array.isArray((apiMarkups as any)?.planSets) ? (apiMarkups as any).planSets : []
  // The effective active set: explicit selection → newest CURRENT → newest → none.
  const effectiveSetId =
    (activeSetId && planSets.some(s => s.id === activeSetId) ? activeSetId : null)
    ?? [...planSets].reverse().find(s => s.status === 'CURRENT')?.id
    ?? planSets.at(-1)?.id
    ?? null
  const activeSet = planSets.find(s => s.id === effectiveSetId) || null
  // Canvas shows ONLY the active set's sheets. Legacy sheets without a setId fall
  // back to the active set so they remain visible.
  const activeSheets: MarkupSheet[] = effectiveSetId
    ? allSheets.filter(s => (s.setId || effectiveSetId) === effectiveSetId)
    : allSheets
  const safeIdx = Math.min(activeSheetIdx, Math.max(0, activeSheets.length - 1))
  // No artificial fallback sheet.
  // The previous code resolved to `{ id: 'empty', name: 'No sheets', ... }`
  // which silently let drawing tools create markup elements with
  // `sheetId: 'empty'`, save them to the backend against a fake sheet,
  // and render a "1/0" sheet counter. That violated the product rule
  // "must work for any drawing set" and produced unverifiable evidence.
  // Now: when no real sheet exists, `activeSheetRef` is null, the
  // canvas renders the bilingual empty state, and every interactive
  // surface is gated on `hasRealActiveSheet`.
  const activeSheetRef: MarkupSheet | null = activeSheets[safeIdx] ?? null
  const hasRealActiveSheet = activeSheets.length > 0 && Boolean(activeSheetRef?.id)

  // Protected image URL for the active sheet, with a cache-buster that changes on
  // retry so a fresh-token reload actually re-fetches (even if the path is same).
  const sheetImageBase = activeSheetRef?.imageUrl
    ? buildProtectedUploadUrl(activeSheetRef.imageUrl, uploadToken)
    : ''
  const sheetImageHref = sheetImageBase && imageReloadNonce
    ? `${sheetImageBase}${sheetImageBase.includes('?') ? '&' : '?'}_r=${imageReloadNonce}`
    : sheetImageBase

  // Plan-set lifecycle (PR-PM5): only a CURRENT set is editable. Archived/
  // superseded sets are read-only (view + export only). No active set info (legacy/
  // brand-new) → editable.
  const activeSetEditable = !activeSet || activeSet.status === 'CURRENT'
  const activeSetReadOnly = Boolean(activeSet) && !activeSetEditable

  // ── Permissions (PR-PM8) ──────────────────────────────────────
  // bluaIQ Plans & Markups is OWNER-ONLY today. Every server route is scoped to
  // project.userId: GET / POST-save / upload / upload-pages / PATCH set-status all
  // do findFirst({ where: { id, userId } }) → 404 for non-owners, and all sit
  // behind the global requireAuth → 401 when unauthenticated; the /api/uploads/*
  // file route verifies the Clerk token (Bearer or ?token=) then owner-scopes by
  // projectId and blocks path traversal. There is NO collaborator/role model in
  // the schema, so we do NOT invent roles — anyone who can load this page IS the
  // project owner (the GET is owner-scoped, so a non-owner gets 404 and never sees
  // the data). These flags resolve to "real owner project" today; demo/non-real
  // projects are read-only (can't persist) and archived/superseded sets are
  // read-only. FUTURE (deferred — requires an approved role/schema model, do NOT
  // implement without it): Owner/Admin → full; Collaborator/Team → configurable
  // edit/view/export; Client/Homeowner → view/export/comment/approve; Contractor/
  // Vendor → limited shared-set access.
  const isOwnerEditable = !isDemo // page loaded ⇒ owner (GET is owner-scoped)
  const planPerms = {
    canViewPlans: true, // a non-owner can't even load this (server 404s)
    canUploadPlans: isOwnerEditable,
    canEditMarkups: isOwnerEditable && activeSetEditable,
    canSaveMarkups: isOwnerEditable,
    canManagePlanSets: isOwnerEditable,
    canArchiveRestoreSupersede: isOwnerEditable,
    canManageLayers: isOwnerEditable && activeSetEditable,
    canExportPlans: true, // viewing/export is read-only-safe → always allowed
  }
  // Honest permission copy. The "no edit permission" string has no FE trigger today
  // (non-owners 404 server-side); it's defined for the future role model. The live
  // non-editable states are demo (SavedProjectOnlyNotice) + read-only sets (banner).
  const noEditPermMsg = isEs
    ? 'No tienes permiso para editar planos en este proyecto.'
    : 'You don’t have permission to edit plans for this project.'

  // The sheet is drawable only once its background image has actually loaded AND
  // the active set is editable (not archived/superseded).
  const activeSheetVisible = hasRealActiveSheet && Boolean(activeSheetRef?.id)
    && loadedSheets.has(activeSheetRef!.id) && activeSetEditable

  // ── Layer management (PR-PM7) ─────────────────────────────────
  // Layer mutations (create/rename/delete/lock) require the layer-management
  // permission (PR-PM8): real owner project AND an editable (CURRENT / no-set) plan
  // set. Archived/superseded sets and demo are read-only.
  const layersEditable = planPerms.canManageLayers
  const activeLayer = layers.find(l => l.id === activeLayerId) || null
  const activeLayerLocked = Boolean(activeLayer?.locked)
  const layerIsLocked = (layerId: string) => Boolean(layers.find(l => l.id === layerId)?.locked)
  // Markups on a layer (across all sheets — layers are global to the markup row).
  const markupsOnLayer = (layerId: string) => elements.filter(el => el.layerId === layerId).length
  // Localized display name for a layer. Prefers the stored nameEs in Spanish, but
  // also shims built-in/auto-generated names that may have been persisted in English
  // by earlier builds ("Notes"/"Structural Notes"/"Layer N") so Spanish never shows
  // stale English. User-chosen names are returned unchanged.
  const localizeLayerName = (layer: MarkupLayer): string => {
    const raw = (isEs ? (layer.nameEs || layer.name) : layer.name) || ''
    if (!isEs) return raw
    if (raw === 'Notes') return 'Notas'
    if (raw === 'Structural Notes') return 'Notas Estructurales'
    if (raw === 'Plumbing') return 'Plomería'
    if (raw === 'Electrical') return 'Eléctrico'
    const m = raw.match(/^Layer (\d+)$/)
    if (m) return `Capa ${m[1]}`
    return raw
  }
  // Drawing is possible only on a visible sheet whose active layer is unlocked.
  const canDraw = activeSheetVisible && !activeLayerLocked

  // ── Export (PR-PM4: set-aware full marked-up PDF) ──────────────
  // Original PDF is NOT stored (client-rendered uploads keep page PNGs only), so
  // the menu honestly shows "Original file not available" — never a fake original.
  const originalPdfAvailable = false

  // iOS (iPhone/iPad incl. iPadOS-on-desktop-UA) — Safari has the most fragile
  // blob-download path, so if a PDF export does fail we show actionable guidance
  // rather than a generic retry message.
  const isIosLike = typeof navigator !== 'undefined' &&
    (/iP(ad|hone|od)/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints || 0) > 1))

  const exportFailMsg = () =>
    isIosLike
      ? (isEs
        ? 'La exportación de PDF puede no ser compatible con este navegador móvil. Usa una computadora o intenta abrir el archivo desde Descargas.'
        : 'PDF export may not be supported by this mobile browser. Please use desktop or try opening the file from Downloads.')
      : (isEs ? 'No se pudo exportar. Intenta de nuevo.' : 'Could not export. Please try again.')
  const partialWarnMsg = (n: number) =>
    isEs ? `${n} hoja(s) no se pudieron incluir y se omitieron.` : `${n} sheet(s) could not be included and were skipped.`

  const resetExportMsgs = () => { setExportMenuOpen(false); setExportError(null); setExportWarn(null) }
  const visibleElementsForSheet = (sheetId: string): MarkupElement[] => {
    const visIds = new Set(layers.filter(l => l.visible).map(l => l.id))
    return elements.filter(el => visIds.has(el.layerId) && el.sheetId === sheetId)
  }

  // Composite one sheet of the active set: current sheet via the live SVG (pixel-
  // match), others programmatically from their visible elements. Returns null on
  // failure (caller counts it as skipped).
  const compositeSetSheet = async (sheet: MarkupSheet, idx: number, token: string | null): Promise<{ dataUrl: string; label: string } | null> => {
    if (!sheet.imageUrl) return null
    try {
      const dataUrl = (svgRef.current && sheet.id === activeSheetRef?.id)
        ? await composeSheetPngDataUrl(svgRef.current, sheetImageHref)
        : await composeSheetPngFromElements(buildProtectedUploadUrl(sheet.imageUrl, token), visibleElementsForSheet(sheet.id))
      return { dataUrl, label: `${isEs ? sheet.nameEs : sheet.name} (${idx + 1}/${activeSheets.length})` }
    } catch (e) {
      console.error('Set-sheet composite error:', e)
      return null
    }
  }

  // PRIMARY: download the ACTIVE plan set as one marked-up PDF (all its sheets,
  // markups per sheet, visible layers only, sheet order, no other sets).
  const handleDownloadSetPdf = async () => {
    resetExportMsgs()
    if (activeSheets.length === 0) { setExportError(exportFailMsg()); return }
    setExporting(true)
    try {
      const token = await refreshUploadToken()
      const pages: { dataUrl: string; label: string }[] = []
      let failed = 0
      for (let i = 0; i < activeSheets.length; i++) {
        const page = await compositeSetSheet(activeSheets[i], i, token)
        if (page) pages.push(page); else failed++
      }
      if (pages.length === 0) { setExportError(exportFailMsg()); return }
      const blob = await buildImagesPdfBlob(pages)
      // iOS Safari: route through a data: URL (same flow as the image export) so the
      // PDF opens in the OS share/save viewer instead of the same tab. Desktop saves
      // directly via the download attribute.
      await downloadPdfBlobSmart(blob, `natami-${sanitizeFilename(activeSet?.name || 'plan-set')}-markup.pdf`, isIosLike)
      if (failed > 0) setExportWarn(partialWarnMsg(failed))
    } catch (e) {
      console.error('Set PDF export error:', e)
      setExportError(exportFailMsg())
    } finally {
      setExporting(false)
    }
  }

  // Print the whole ACTIVE set.
  const handlePrintSet = async () => {
    resetExportMsgs()
    if (activeSheets.length === 0) { setExportError(exportFailMsg()); return }
    setExporting(true)
    try {
      const token = await refreshUploadToken()
      const urls: string[] = []
      let failed = 0
      for (let i = 0; i < activeSheets.length; i++) {
        const page = await compositeSetSheet(activeSheets[i], i, token)
        if (page) urls.push(page.dataUrl); else failed++
      }
      if (urls.length === 0) { setExportError(exportFailMsg()); return }
      printImageDataUrls(urls)
      if (failed > 0) setExportWarn(partialWarnMsg(failed))
    } catch (e) {
      console.error('Set print error:', e)
      setExportError(exportFailMsg())
    } finally {
      setExporting(false)
    }
  }

  // SECONDARY: current sheet image (PNG).
  const handleExportImage = async () => {
    resetExportMsgs()
    if (!svgRef.current || !activeSheetRef?.imageUrl) { setExportError(exportFailMsg()); return }
    setExporting(true)
    try {
      const dataUrl = await composeSheetPngDataUrl(svgRef.current, sheetImageHref)
      downloadDataUrl(dataUrl, `natami-${sanitizeFilename(activeSheetRef.name || 'sheet')}.png`)
    } catch (e) {
      console.error('Sheet image export error:', e)
      setExportError(exportFailMsg())
    } finally {
      setExporting(false)
    }
  }

  // Print the current sheet only (convenience).
  const handlePrintSheet = async () => {
    resetExportMsgs()
    if (!svgRef.current || !activeSheetRef?.imageUrl) { setExportError(exportFailMsg()); return }
    setExporting(true)
    try {
      const dataUrl = await composeSheetPngDataUrl(svgRef.current, sheetImageHref)
      printImageDataUrls([dataUrl])
    } catch (e) {
      console.error('Sheet print error:', e)
      setExportError(exportFailMsg())
    } finally {
      setExporting(false)
    }
  }

  // ── Plan-set lifecycle (PR-PM5): archive / restore / supersede ──────────
  // Every lifecycle change is confirmed first (no silent status change).
  // True when archiving/superseding this set would leave the project with zero
  // CURRENT sets (FE mirror of the backend NO_CURRENT_LEFT guard).
  const isOnlyCurrentSet = (set: PlanSet) =>
    set.status === 'CURRENT' && planSets.filter(s => s.status === 'CURRENT').length <= 1

  const requestSetAction = (set: PlanSet, action: 'archive' | 'restore' | 'supersede') => {
    // PR-PM8: plan-set lifecycle requires the owner permission (defense-in-depth —
    // these controls only render for real owner projects; the server also 404s).
    if (!planPerms.canArchiveRestoreSupersede) return
    setUploadError(null); setUploadSuccessMsg(null)
    // FE guard: don't open a misleading "Archive set" confirm for the only current
    // set — block immediately with a clear reason (backend NO_CURRENT_LEFT remains).
    if ((action === 'archive' || action === 'supersede') && isOnlyCurrentSet(set)) {
      setUploadError(isEs
        ? 'No puedes archivar el único set actual. Primero crea o restaura otro set actual.'
        : 'You can’t archive the only current set. Create or restore another current set first.')
      return
    }
    setPendingSetAction({ setId: set.id, setName: set.name, action })
  }

  const confirmSetAction = async () => {
    if (!pendingSetAction) return
    const { setId, action } = pendingSetAction
    const status = action === 'restore' ? 'CURRENT' : action === 'archive' ? 'ARCHIVED' : 'SUPERSEDED'
    setPendingSetAction(null)
    setSettingSetStatus(true)
    setUploadError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/drawing-markups/sets/${setId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ status }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        // Guard: can't archive the only current set — clear bilingual copy.
        if (err?.code === 'NO_CURRENT_LEFT') {
          setUploadError(isEs
            ? 'No puedes archivar el único set actual. Marca otro set como actual primero.'
            : 'You can’t archive the only current set. Make another set current first.')
        } else {
          setUploadError(isEs ? 'No se pudo cambiar el estado del set. Intenta de nuevo.' : 'Could not change the set status. Please try again.')
        }
        return
      }
      const data = await res.json()
      // Reflect the new statuses immediately, then refetch authoritative state.
      if (Array.isArray(data.planSets)) {
        queryClient.setQueryData(['markups', projectId], (old: any) => old ? { ...old, planSets: data.planSets } : old)
      }
      // Restore → that set becomes the active (current) view.
      if (action === 'restore') { setActiveSetId(setId); setActiveSheetIdx(0) }
      setUploadSuccessMsg(action === 'restore'
        ? (isEs ? 'Set restaurado como actual' : 'Set restored as current')
        : action === 'archive'
          ? (isEs ? 'Set archivado' : 'Set archived')
          : (isEs ? 'Set marcado como reemplazado' : 'Set marked as superseded'))
      setTimeout(() => setUploadSuccessMsg(null), 4000)
      queryClient.invalidateQueries({ queryKey: ['markups', projectId] })
    } catch (e) {
      console.error('Set status change error:', e)
      setUploadError(isEs ? 'No se pudo cambiar el estado del set. Intenta de nuevo.' : 'Could not change the set status. Please try again.')
    } finally {
      setSettingSetStatus(false)
    }
  }

  // Accepted plan format: PDF ONLY (UT-SUBIDA-1, sealed & reversible). The engine
  // quantifies from vector PDF, not from raster images, so accepting an image
  // would strand the user with a file that cannot be quantified — the dead-end
  // the product fights. A non-PDF is rejected here with the clear bilingual
  // "Unsupported file type" message (not a server 500).
  //
  // FUTURE DOOR — UT-SUBIDA-1: images are architecture kept ready but NOT
  // activated. To enable them tomorrow, restore image acceptance here
  //   (`|| t.startsWith('image/')` + the raster extensions
  //    /\.(pdf|png|jpe?g|webp|gif|bmp|tiff?)$/i) AND widen the accept="" on the
  // file input below back to `.pdf,image/*`. The server-side image branch already
  // exists (applyPlanSetUpload) — this client gate is the only switch to flip.
  const isSupportedPlan = (type: string, name: string): boolean => {
    const t = (type || '').toLowerCase()
    if (t === 'application/pdf') return true
    return /\.pdf$/i.test(name || '')
  }

  // Distinct, actionable upload-error copy by failure kind (QA): unsupported
  // file vs PDF-processing/rendering failure vs storage/unknown failure.
  const planUploadErrorMessage = (code: string | undefined): string => {
    switch (code) {
      case 'UNSUPPORTED':
        return isEs ? 'Tipo de archivo no compatible' : 'Unsupported file type'
      case 'PDF_PROCESSING':
        return isEs
          ? 'No pudimos procesar este PDF. Intenta con otro archivo o contacta soporte.'
          : 'We could not process this PDF. Try another file or contact support.'
      default: // STORAGE / unknown / network
        return isEs ? 'No se pudo subir el plano. Intenta de nuevo.' : 'Could not upload the plan. Please try again.'
    }
  }

  // File-input handler → builds a multi-file pending batch + set decision.
  const handlePlanUpload = (files: File[]) => {
    requestBatchUpload(files)
  }

  // Persist current elements/layers to backend. Sheets come from the upload path.
  //
  // Safety invariant: elements whose sheetId is not a real, currently-known
  // sheet (i.e. anything left over from the legacy `sheetId: 'empty'`
  // fallback, or any sheetId not present in `activeSheets`) MUST NOT be
  // persisted. We strip those defensively here even though the new
  // mouse-handler guard prevents them from being created in the first
  // place — both belts and braces, because once the value reaches the
  // server it becomes audit evidence.
  const saveMutation = useMutation({
    mutationFn: async () => {
      const knownSheetIds = new Set(
        activeSheets
          .map(s => s?.id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0 && id !== 'empty'),
      )
      const safeElements = elements.filter(
        el => typeof el.sheetId === 'string' && el.sheetId !== 'empty' && knownSheetIds.has(el.sheetId),
      )
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/drawing-markups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ elements: safeElements, layers }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.detail || `Save failed (${res.status})`)
      }
      return res.json()
    },
    onMutate: () => setSaveStatus('saving'),
    onSuccess: () => {
      setSaveStatus('saved')
      queryClient.invalidateQueries({ queryKey: ['markups', projectId] })
      setTimeout(() => setSaveStatus('idle'), 2000)
    },
    onError: () => {
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    },
  })

  const visibleLayerIds = new Set(layers.filter(l => l.visible).map(l => l.id))
  // No real active sheet → nothing to render. Without this guard the
  // filter would short-circuit on `activeSheetRef.id === 'empty'` and
  // render any legacy element with `sheetId: 'empty'` that might still
  // be sitting in state.
  const visibleElements = hasRealActiveSheet && activeSheetRef
    ? elements.filter(el => visibleLayerIds.has(el.layerId) && el.sheetId === activeSheetRef.id)
    : []

  // ── SVG event helpers ──────────────────────────
  const getSvgPoint = (e: React.MouseEvent) => {
    const svg = svgRef.current
    if (!svg) return { x: 0, y: 0 }
    // Map screen coords → SVG user space (viewBox 0 0 700 420). getScreenCTM
    // accounts for CSS scaling, horizontal centering, scroll, browser zoom and
    // devicePixelRatio in one step — the previous raw `clientX - rect.left`
    // returned CSS pixels, so on desktop (SVG wider than 700) every shape landed
    // offset to the right of the cursor. Shared by pen/line/rect/circle/arrow/text.
    const ctm = svg.getScreenCTM()
    if (ctm) {
      const sp = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse())
      return { x: sp.x, y: sp.y }
    }
    // Fallback: scale the bounding-rect offset into viewBox units.
    const rect = svg.getBoundingClientRect()
    const sx = rect.width ? VIEWBOX_W / rect.width : 1
    const sy = rect.height ? VIEWBOX_H / rect.height : 1
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    // Drawing is inert until the sheet's image is actually visible — never start a
    // stroke on an empty, failed, or still-loading sheet (would create an element
    // bound to a sheet the user can't see). Also blocked when the active layer is
    // locked (PR-PM7) — canDraw folds both conditions.
    if (!canDraw) return
    if (activeTool === 'select') return
    const pt = getSvgPoint(e)
    setIsDrawing(true)
    setDrawStart(pt)
    if (activeTool === 'pen') setPenPoints([pt])
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    // Empty-state safety: ignore any in-flight stroke if the sheet
    // disappeared (e.g. API hydration cleared sheets out from under us).
    if (!hasRealActiveSheet) return
    if (!isDrawing || !drawStart) return
    if (activeTool === 'pen') {
      const pt = getSvgPoint(e)
      setPenPoints(prev => [...prev, pt])
    }
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    // Empty-state safety: never create an element when no real sheet
    // exists. Combined with the save-mutation filter this guarantees
    // sheetId='empty' (or any other fake id) cannot reach state or DB.
    if (!hasRealActiveSheet || !activeSheetRef) {
      // Reset any partial drawing state defensively.
      setIsDrawing(false)
      setDrawStart(null)
      setPenPoints([])
      return
    }
    if (!isDrawing || !drawStart) return
    const pt = getSvgPoint(e)
    const newId = `el-${Date.now()}`

    let newEl: MarkupElement | null = null

    const sid = activeSheetRef.id
    if (activeTool === 'pen' && penPoints.length > 1) {
      newEl = { id: newId, type: 'pen', points: penPoints, color: activeColor, strokeWidth: activeStroke, layerId: activeLayerId, sheetId: sid }
    } else if (activeTool === 'line') {
      newEl = { id: newId, type: 'line', points: [drawStart, pt], color: activeColor, strokeWidth: activeStroke, layerId: activeLayerId, sheetId: sid }
    } else if (activeTool === 'arrow') {
      newEl = { id: newId, type: 'arrow', points: [drawStart, pt], color: activeColor, strokeWidth: activeStroke, layerId: activeLayerId, sheetId: sid }
    } else if (activeTool === 'rectangle') {
      newEl = { id: newId, type: 'rectangle', x: Math.min(drawStart.x, pt.x), y: Math.min(drawStart.y, pt.y), width: Math.abs(pt.x - drawStart.x), height: Math.abs(pt.y - drawStart.y), color: activeColor, strokeWidth: activeStroke, layerId: activeLayerId, sheetId: sid }
    } else if (activeTool === 'circle') {
      const cx = (drawStart.x + pt.x) / 2
      const cy = (drawStart.y + pt.y) / 2
      const rx = Math.abs(pt.x - drawStart.x) / 2
      const ry = Math.abs(pt.y - drawStart.y) / 2
      newEl = { id: newId, type: 'circle', x: cx, y: cy, width: rx * 2, height: ry * 2, color: activeColor, strokeWidth: activeStroke, layerId: activeLayerId, sheetId: sid }
    } else if (activeTool === 'text') {
      const text = prompt(t('drawing.prompts.enterText'))
      if (text) {
        newEl = { id: newId, type: 'text', x: drawStart.x, y: drawStart.y, text, color: activeColor, strokeWidth: activeStroke, layerId: activeLayerId, sheetId: sid }
      }
    }

    if (newEl) setElements(prev => [...prev, newEl!])
    setIsDrawing(false)
    setDrawStart(null)
    setPenPoints([])
  }

  const deleteSelected = () => {
    if (!selectedId) return
    // PR-PM7 defense-in-depth: never delete a markup on a locked layer.
    const sel = elements.find(e => e.id === selectedId)
    if (sel && layerIsLocked(sel.layerId)) return
    setElements(prev => prev.filter(el => el.id !== selectedId))
    setSelectedId(null)
  }

  const toggleLayer = (layerId: string) => {
    // Visibility is a view/export concern → allowed even on read-only sets.
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, visible: !l.visible } : l))
  }

  // PR-PM10 state hygiene: exactly ONE layer action (rename or delete) may be active
  // at a time. Every opener clears the OTHER action's state + any stale error first
  // so a delete modal can never be left stuck behind a rename input (or vice-versa),
  // and the user can't get trapped. This is the single place that resets it all.
  const resetLayerActionState = () => {
    setRenamingLayerId(null)
    setRenameValue('')
    setPendingLayerDelete(null)
    setLayerError(null)
  }
  // Close whatever layer action is currently open (mobile sheet X / desktop cancel).
  const closeLayerAction = () => resetLayerActionState()

  const toggleLockLayer = (layerId: string) => {
    if (!layersEditable) return
    // Lock is a distinct action → clear any open rename/delete first (one at a time).
    resetLayerActionState()
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, locked: !l.locked } : l))
  }

  // Create a layer with a sensible default name, then open rename (no native
  // prompt()). Blocked on read-only sets. Clears any open delete/rename first so the
  // create→rename flow never overlaps a stuck delete modal (PR-PM10).
  const addLayer = () => {
    if (!layersEditable) return
    resetLayerActionState()
    const n = layers.length + 1
    const newLayer: MarkupLayer = {
      id: `layer-${Date.now()}`,
      name: `Layer ${n}`,
      nameEs: `Capa ${n}`,
      visible: true,
      color: activeColor,
      locked: false,
    }
    setLayers(prev => [...prev, newLayer])
    setActiveLayerId(newLayer.id)
    setRenamingLayerId(newLayer.id)
    setRenameValue(localizeLayerName(newLayer))
  }

  // ── Rename ──
  const startRenameLayer = (layer: MarkupLayer) => {
    if (!layersEditable) return
    // Clear any open delete modal/action first (one action at a time, PR-PM10).
    resetLayerActionState()
    setRenamingLayerId(layer.id)
    setRenameValue(localizeLayerName(layer))
  }
  const commitRenameLayer = () => {
    if (!renamingLayerId) { return }
    const name = renameValue.trim()
    // Empty name keeps the previous name (no silent blanking). Set both EN/ES to
    // the user's text so the renamed layer reads the same in either language.
    if (name) setLayers(prev => prev.map(l => l.id === renamingLayerId ? { ...l, name, nameEs: name } : l))
    setRenamingLayerId(null)
    setRenameValue('')
  }
  const cancelRenameLayer = () => { setRenamingLayerId(null); setRenameValue('') }

  // ── Delete (safe) ──
  const requestDeleteLayer = (layer: MarkupLayer) => {
    if (!layersEditable) return
    // Clear any open rename input/action first (one action at a time, PR-PM10).
    resetLayerActionState()
    // Close the layers panel so the delete confirmation renders above it regardless
    // of z-index. Without this, the desktop modal (z-[60]) and the mobile action
    // sheet can be obscured when the layers panel (z-50) stays open in the same
    // stacking context, particularly in Chrome DevTools simulation where
    // pointer: coarse is not emulated and the desktop modal renders at z-50.
    setShowLayers(false)
    // Last-layer protection: there must always be at least one layer.
    if (layers.length <= 1) {
      setLayerError(isEs
        ? 'No puedes borrar la última capa. Crea otra capa primero.'
        : 'You can’t delete the last layer. Create another layer first.')
      return
    }
    const count = markupsOnLayer(layer.id)
    setDeleteChoice('move')
    setMoveTargetId(layers.find(l => l.id !== layer.id)?.id || '')
    setPendingLayerDelete({ layerId: layer.id, name: localizeLayerName(layer), count })
  }
  const confirmDeleteLayer = () => {
    if (!pendingLayerDelete) return
    const { layerId, count } = pendingLayerDelete
    if (count > 0 && deleteChoice === 'move' && moveTargetId) {
      // Move this layer's markups to the chosen layer (preserve them).
      setElements(prev => prev.map(el => el.layerId === layerId ? { ...el, layerId: moveTargetId } : el))
    } else if (count > 0 && deleteChoice === 'purge') {
      // Delete ONLY the markups on this layer (other layers/sheets untouched).
      setElements(prev => prev.filter(el => el.layerId !== layerId))
    }
    const remaining = layers.filter(l => l.id !== layerId)
    setLayers(remaining)
    // If the active layer was removed, pick a safe replacement.
    if (activeLayerId === layerId) setActiveLayerId(remaining[0]?.id || '')
    setPendingLayerDelete(null)
  }

  // Shared delete-layer body — rendered identically by the DESKTOP modal and the
  // MOBILE full-screen action sheet (only one mounts at a time, gated by
  // isCoarsePointer), so the move/purge/select testids live in a single place.
  const renderLayerDeleteBody = () => {
    if (!pendingLayerDelete) return null
    return (
      <>
        <p className="text-[12px] font-[600] text-[#6B6660]">
          {isEs ? 'Capa' : 'Layer'}: <span className="font-[800] text-[#1C1A17]">{pendingLayerDelete.name}</span>
          {' · '}
          <span data-testid="layer-delete-count">{pendingLayerDelete.count}</span> {isEs ? 'marca(s)' : 'markup(s)'}
        </p>
        {pendingLayerDelete.count > 0 ? (
          <div className="space-y-2">
            <p className="text-[12px] font-[700] text-[#1C1A17]">
              {isEs ? 'Esta capa tiene marcas. Elige qué hacer:' : 'This layer has markups. Choose what to do:'}
            </p>
            <label className="flex items-start gap-2 text-[12px] font-[600] text-[#1C1A17] cursor-pointer">
              <input data-testid="layer-delete-move-radio" type="radio" name="layerDelete" checked={deleteChoice === 'move'} onChange={() => setDeleteChoice('move')} className="mt-0.5" />
              <span className="flex-1">
                {isEs ? 'Mover marcas a:' : 'Move markups to:'}
                <select
                  data-testid="layer-delete-move-target"
                  value={moveTargetId}
                  onChange={e => setMoveTargetId(e.target.value)}
                  onClick={e => { e.stopPropagation(); setDeleteChoice('move') }}
                  className="mt-1 block w-full text-[12px] font-[700] border border-[#ECEAE4] rounded-lg px-2 py-2 outline-none"
                >
                  {layers.filter(l => l.id !== pendingLayerDelete.layerId).map(l => (
                    <option key={l.id} value={l.id}>{localizeLayerName(l)}</option>
                  ))}
                </select>
              </span>
            </label>
            <label className="flex items-start gap-2 text-[12px] font-[600] text-red-700 cursor-pointer">
              <input data-testid="layer-delete-purge-radio" type="radio" name="layerDelete" checked={deleteChoice === 'purge'} onChange={() => setDeleteChoice('purge')} className="mt-0.5" />
              <span className="flex-1">
                {isEs
                  ? `Borrar la capa y sus ${pendingLayerDelete.count} marca(s)`
                  : `Delete the layer and its ${pendingLayerDelete.count} markup(s)`}
              </span>
            </label>
          </div>
        ) : (
          <p className="text-[12px] font-[600] text-[#6B6660]">
            {isEs ? 'Esta capa no tiene marcas. ¿Borrarla?' : 'This layer has no markups. Delete it?'}
          </p>
        )}
      </>
    )
  }
  // Shared confirm-button label for delete (move vs purge vs empty).
  const layerDeleteConfirmText = (): string => {
    if (!pendingLayerDelete) return ''
    if (pendingLayerDelete.count > 0 && deleteChoice === 'purge') return isEs ? 'Borrar capa y marcas' : 'Delete layer & markups'
    if (pendingLayerDelete.count > 0) return isEs ? 'Mover y borrar capa' : 'Move & delete layer'
    return isEs ? 'Borrar capa' : 'Delete layer'
  }
  const layerDeleteIsPurge = () => Boolean(pendingLayerDelete && pendingLayerDelete.count > 0 && deleteChoice === 'purge')

  // ── KPIs ──────────────────────────────────────
  const totalMarkups = elements.length
  const layerCount = layers.length
  const visibleCount = layers.filter(l => l.visible).length

  // ── Render SVG element ────────────────────────
  const renderElement = (el: MarkupElement) => {
    const isSelected = selectedId === el.id
    const strokeDash = isSelected ? '4 2' : undefined

    const handleClick = (e: React.MouseEvent) => {
      if (activeTool === 'select') {
        e.stopPropagation()
        // PR-PM7: markups on a locked layer cannot be selected (→ not editable/
        // deletable). Read-only set already blocks all editing upstream.
        if (layerIsLocked(el.layerId)) return
        setSelectedId(el.id)
      }
    }

    switch (el.type) {
      case 'pen':
        if (!el.points || el.points.length < 2) return null
        return (
          <polyline
            key={el.id}
            points={el.points.map(p => `${p.x},${p.y}`).join(' ')}
            fill="none" stroke={el.color} strokeWidth={el.strokeWidth}
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={strokeDash} onClick={handleClick} className="cursor-pointer"
          />
        )
      case 'line':
        if (!el.points || el.points.length < 2) return null
        return (
          <line
            key={el.id}
            x1={el.points[0].x} y1={el.points[0].y} x2={el.points[1].x} y2={el.points[1].y}
            stroke={el.color} strokeWidth={el.strokeWidth}
            strokeDasharray={strokeDash} onClick={handleClick} className="cursor-pointer"
          />
        )
      case 'arrow':
        if (!el.points || el.points.length < 2) return null
        return (
          <g key={el.id} onClick={handleClick} className="cursor-pointer">
            <defs>
              <marker id={`arrow-${el.id}`} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill={el.color} />
              </marker>
            </defs>
            <line
              x1={el.points[0].x} y1={el.points[0].y} x2={el.points[1].x} y2={el.points[1].y}
              stroke={el.color} strokeWidth={el.strokeWidth}
              markerEnd={`url(#arrow-${el.id})`} strokeDasharray={strokeDash}
            />
          </g>
        )
      case 'rectangle':
        return (
          <rect
            key={el.id}
            x={el.x} y={el.y} width={el.width} height={el.height}
            fill="none" stroke={el.color} strokeWidth={el.strokeWidth}
            strokeDasharray={strokeDash} onClick={handleClick} className="cursor-pointer"
          />
        )
      case 'circle':
        return (
          <ellipse
            key={el.id}
            cx={el.x} cy={el.y} rx={(el.width || 0) / 2} ry={(el.height || 0) / 2}
            fill="none" stroke={el.color} strokeWidth={el.strokeWidth}
            strokeDasharray={strokeDash} onClick={handleClick} className="cursor-pointer"
          />
        )
      case 'text':
        return (
          <text
            key={el.id}
            x={el.x} y={el.y} fill={el.color}
            fontSize="13" fontWeight="700" fontFamily="sans-serif"
            onClick={handleClick} className="cursor-pointer select-none"
            textDecoration={isSelected ? 'underline' : undefined}
          >
            {el.text}
          </text>
        )
      default:
        return null
    }
  }

  return (
    <div className="p-3 sm:p-5 max-w-6xl mx-auto space-y-4">
      {/* Header — wraps on mobile so action buttons never overflow off-screen. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <PenTool size={18} />
            {t('drawing.title')}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {totalMarkups} {t('drawing.subtitle.annotations')} · {activeSheets.length} {t('drawing.subtitle.sheets')} · {layerCount} {t('drawing.subtitle.layers')}
          </p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          {/* PR #138A / PR-PM8: upload + save require the owner edit permission
              (false in demo, which targets a non-existent project). Demo users
              still see DEMO_SHEETS/LAYERS/ELEMENTS read-only. */}
          {planPerms.canUploadPlans && (
            <>
              <button
                onClick={() => planFileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#E6961A] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full disabled:opacity-40 transition-colors"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? (isEs ? 'Subiendo…' : 'Uploading…') : t('drawing.upload')}
              </button>
              {/* PDF only — UT-SUBIDA-1 (widen back to `.pdf,image/*` when the
                  image layer is activated; the client isSupportedPlan gate above
                  is the paired switch). */}
              <input ref={planFileRef} type="file" accept=".pdf" multiple className="hidden"
                onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) handlePlanUpload(fs); e.target.value = '' }} />
            </>
          )}
          {takeoffAvailable && takeoffFiles && takeoffFiles.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowTakeoffFiles(!showTakeoffFiles)}
                className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
              >
                <FileText size={14} /> {isEs ? 'Importar de Takeoff' : 'Import from Takeoff'}
              </button>
              {showTakeoffFiles && (
                <div className="absolute right-0 top-12 z-20 bg-white border border-[#ECEAE4] rounded-lg shadow-lg py-1 min-w-[280px]">
                  {takeoffFiles.map(f => (
                    <button
                      key={f.id}
                      onClick={() => handleImportTakeoffFile(f)}
                      className="block w-full text-left px-3 py-2 hover:bg-[#F2F0EB] transition-colors"
                    >
                      <div className="text-[12px] font-[700] text-[#1C1A17]">{f.originalFilename}</div>
                      <div className="text-[9px] font-[600] text-[#9E9890]">{f.revisionLabel || ''} · {new Date(f.createdAt).toLocaleDateString()}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="relative">
            <button
              data-testid="plan-export-button"
              onClick={() => { setExportError(null); setExportMenuOpen(o => !o) }}
              disabled={!hasRealActiveSheet || exporting}
              title={!hasRealActiveSheet ? (isEs ? 'Sube planos para exportar' : 'Upload plans to export') : undefined}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors disabled:opacity-40"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {t('drawing.export')}
            </button>
            {exportMenuOpen && hasRealActiveSheet && (
              <>
                {/* click-away */}
                <div className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent" onClick={() => setExportMenuOpen(false)} />
                {/* Mobile: a bottom sheet inset 1rem from both edges → always fits
                    the viewport, never overflows horizontally. Desktop (sm+): the
                    original right-aligned dropdown anchored under the button. */}
                <div data-testid="plan-export-menu" className="fixed inset-x-4 bottom-4 z-50 w-auto sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:mt-2 sm:w-72 bg-white border border-[#ECEAE4] rounded-xl shadow-lg overflow-hidden">
                  {/* PRIMARY: full marked-up PDF of the ACTIVE set */}
                  <button data-testid="export-set-pdf" onClick={handleDownloadSetPdf}
                    className="w-full text-left text-[12px] font-[800] text-white bg-[#06539B] hover:bg-[#063e73] px-3 py-2.5">
                    {isEs ? 'Descargar PDF marcado' : 'Download marked-up PDF'}
                  </button>
                  {/* iOS/Safari: blob downloads may open in the same tab instead of
                      downloading. Show a recoverable note so users aren't stranded. */}
                  {isIosLike && (
                    <p data-testid="export-ios-note" className="text-[10px] font-[600] text-[#9E9890] px-3 pb-2 pt-1">
                      {isEs
                        ? 'En iPhone/Safari, el PDF puede abrirse en esta misma pestaña. Usa Regresar para volver a NATAMI.'
                        : 'On iPhone/Safari, the PDF may open in this tab. Use Back to return to NATAMI.'}
                    </p>
                  )}
                  <button data-testid="export-print-set" onClick={handlePrintSet}
                    className="w-full text-left text-[12px] font-[700] text-[#1C1A17] px-3 py-2.5 hover:bg-[#F2F0EB] border-t border-[#F2F0EB]">
                    {isEs ? 'Imprimir set actual' : 'Print current set'}
                  </button>
                  <button data-testid="export-print" onClick={handlePrintSheet}
                    className="w-full text-left text-[12px] font-[700] text-[#1C1A17] px-3 py-2.5 hover:bg-[#F2F0EB] border-t border-[#F2F0EB]">
                    {isEs ? 'Imprimir hoja actual' : 'Print current sheet'}
                  </button>
                  {/* SECONDARY: current-sheet image */}
                  <button data-testid="export-current-image" onClick={handleExportImage}
                    className="w-full text-left text-[11px] font-[600] text-[#6B6660] px-3 py-2.5 hover:bg-[#F2F0EB] border-t border-[#F2F0EB]">
                    {isEs ? 'Descargar imagen de hoja actual' : 'Download current sheet image'}
                  </button>
                  {/* Original PDF: not stored → honest note */}
                  {originalPdfAvailable ? null : (
                    <div data-testid="export-original-unavailable"
                      className="w-full text-left text-[11px] font-[600] text-[#9E9890] px-3 py-2.5 border-t border-[#F2F0EB]">
                      {isEs ? 'Archivo original no disponible' : 'Original file not available'}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {/* PR #138A / PR-PM8: save requires the owner save permission (false in
              demo → POST would hit a non-existent project). Hidden when not allowed.
              The disabled expression keeps the empty-state + read-only-set guards. */}
          {planPerms.canSaveMarkups && (
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveStatus === 'saving' || !hasRealActiveSheet || activeSetReadOnly}
              title={!hasRealActiveSheet
                ? (isEs
                  ? 'Sube planos para guardar marcaciones'
                  : 'Upload plans to save markups')
                : activeSetReadOnly
                  ? (isEs ? 'Set de solo lectura — restáuralo para guardar' : 'Read-only set — restore it to save')
                  : undefined}
              className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full disabled:opacity-40"
            >
              {saveStatus === 'saving' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              {saveStatus === 'saving'
                ? t('drawing.saving')
                : saveStatus === 'saved'
                  ? t('drawing.saved')
                  : saveStatus === 'error'
                    ? t('drawing.error')
                    : t('drawing.save')}
            </button>
          )}
        </div>
      </div>

      {/* PR #138A: demo banner explaining why upload/save are absent. */}
      {isDemo && (
        <SavedProjectOnlyNotice
          testId="markup-demo-notice"
          bodyEn="Plan upload and markup saving are available in saved projects. Create or open a real project to edit drawings."
          bodyEs="La carga de planos y el guardado de marcaciones están disponibles en proyectos guardados. Crea o abre un proyecto real para editar planos."
        />
      )}

      {uploadError && (
        <div data-testid="plan-upload-error" className="text-[11px] font-[700] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {uploadError}
        </div>
      )}

      {uploadSuccessMsg && (
        <div data-testid="plan-upload-success" className="text-[11px] font-[700] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
          {uploadSuccessMsg}
        </div>
      )}

      {exportError && (
        <div data-testid="plan-export-error" className="text-[11px] font-[700] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {exportError}
        </div>
      )}

      {exportWarn && (
        <div data-testid="plan-export-warn" className="text-[11px] font-[700] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {exportWarn}
        </div>
      )}

      {/* Pending batch + plan-set decision (PR-PM3). Multi-file review, then a
          4-way choice (Create new set / Add to current / Replace specific / Cancel)
          so an upload never silently replaces or destroys an existing set. */}
      {batchStep && pendingBatch.length >= 0 && (
        <div data-testid="plan-batch-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            {batchStep === 'list' && (
              <>
                <div className="text-sm font-[800] text-gray-900">{isEs ? 'Archivos por subir' : 'Files to upload'}</div>
                <div data-testid="plan-pending-list" className="divide-y divide-gray-100 max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                  {pendingBatch.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2 text-[12px]">
                      <FileText size={13} className="text-[#9E9890] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-[700] text-[#1C1A17] truncate">{p.file.name}</div>
                        <div className="text-[10px] text-[#9E9890]">
                          {(p.file.type || (/\.pdf$/i.test(p.file.name) ? 'application/pdf' : 'image'))} · {(p.file.size / 1024 / 1024).toFixed(2)} MB · {p.pages == null ? (isEs ? '… págs' : '… pages') : `${p.pages} ${isEs ? 'hoja(s)' : 'sheet(s)'}`}
                        </div>
                      </div>
                      <button data-testid="plan-pending-remove" onClick={() => removePendingFile(idx)} className="p-1 text-[#9E9890] hover:text-red-600" title={isEs ? 'Quitar' : 'Remove'}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
                {/* The set decision is shown DIRECTLY here (no ambiguous "Upload"):
                    when sets already exist, the 4-way is explicit and unavoidable;
                    with no sets, the first upload honestly creates the first set. */}
                {planSets.length > 0 ? (
                  <>
                    <div className="text-[11px] font-[800] text-gray-900 pt-1">{isEs ? '¿Qué hacer con estos archivos?' : 'What to do with these files?'}</div>
                    <div className="flex flex-col gap-2">
                      <button data-testid="plan-choose-new" onClick={() => runBatchUpload('new')} disabled={pendingBatch.length === 0} className="w-full text-left text-[12px] font-[700] text-white bg-[#00BCD4] hover:bg-[#00ACC1] rounded-lg min-h-[44px] px-3 py-2 disabled:opacity-40">{isEs ? 'Crear nuevo set / revisión' : 'Create new plan set / revision'}</button>
                      <button data-testid="plan-choose-add" onClick={() => runBatchUpload('add', effectiveSetId)} disabled={pendingBatch.length === 0 || !effectiveSetId} className="w-full text-left text-[12px] font-[700] text-[#1C1A17] bg-[#F2F0EB] hover:bg-[#E8E5DF] rounded-lg min-h-[44px] px-3 py-2 disabled:opacity-40">{isEs ? 'Agregar al set actual' : 'Add to current set'}</button>
                      <button data-testid="plan-choose-replace" onClick={() => setBatchStep('pick-replace')} disabled={pendingBatch.length === 0} className="w-full text-left text-[12px] font-[700] text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg min-h-[44px] px-3 py-2 disabled:opacity-40">{isEs ? 'Reemplazar set específico' : 'Replace specific set'}</button>
                      <button data-testid="plan-choose-cancel" onClick={cancelBatch} className="w-full text-left text-[12px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg min-h-[44px] px-3 py-2">{isEs ? 'Cancelar' : 'Cancel'}</button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Bug 1 (Codex): the first upload FELT like it wouldn't
                        go through — the confirmation step was unlabeled, so it
                        was unclear a second click was needed. Acknowledge the
                        selection explicitly and make the CTA name the action
                        (confirm + upload), not just "create set". Copy says "PDF"
                        (not "archivo/file") per UT-SUBIDA-1: only PDF is accepted
                        today, so naming a broader "file" would invite an image
                        the engine cannot quantify — the dead-end the product
                        fights. */}
                    <p data-testid="plan-batch-confirm-hint" className="text-[12px] font-[600] text-[#1C1A17] bg-[#F0FCFE] border border-[#00BCD4]/40 rounded-lg px-3 py-2">
                      {isEs
                        ? `${pendingBatch.length} PDF ${pendingBatch.length === 1 ? 'seleccionado' : 'seleccionados'}. Confirme para crear el set de planos.`
                        : `${pendingBatch.length} ${pendingBatch.length === 1 ? 'PDF' : 'PDFs'} selected. Confirm to create the plan set.`}
                    </p>
                    <div className="flex gap-2 justify-end">
                      <button data-testid="plan-batch-cancel" onClick={cancelBatch} className="text-[12px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg min-h-[44px] px-3 py-2">{isEs ? 'Cancelar' : 'Cancel'}</button>
                      <button data-testid="plan-batch-create-first" onClick={() => runBatchUpload('new')} disabled={pendingBatch.length === 0} className="text-[12px] font-[700] text-white bg-[#06539B] hover:bg-[#063e73] rounded-lg min-h-[44px] px-3 py-2 disabled:opacity-40">{isEs ? 'Crear set y subir' : 'Create set and upload'}</button>
                    </div>
                  </>
                )}
              </>
            )}

            {batchStep === 'pick-replace' && (
              <>
                <div className="text-sm font-[800] text-gray-900">{isEs ? 'Elegir set a reemplazar' : 'Choose set to replace'}</div>
                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                  {planSets.map(ps => (
                    <button key={ps.id} data-testid="plan-replace-pick" onClick={() => { setReplaceTargetSetId(ps.id); setBatchStep('warn-replace') }} className="block w-full text-left min-h-[44px] px-3 py-2 hover:bg-[#F2F0EB]">
                      <div className="text-[12px] font-[700] text-[#1C1A17] truncate">{ps.name}</div>
                      <div className="text-[10px] text-[#9E9890]">{ps.sheetCount ?? 0} {isEs ? 'hojas' : 'sheets'}{ps.revisionLabel ? ` · ${ps.revisionLabel}` : ''}</div>
                    </button>
                  ))}
                </div>
                <div className="flex justify-end"><button onClick={() => setBatchStep('list')} className="text-[12px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-2">{isEs ? 'Atrás' : 'Back'}</button></div>
              </>
            )}

            {batchStep === 'warn-replace' && (
              <>
                <div className="text-sm font-[800] text-red-700">{isEs ? 'Reemplazar set existente' : 'Replace existing set'}</div>
                <p className="text-[12px] text-gray-700">
                  {isEs
                    ? 'Vas a reemplazar este set de planos por el nuevo archivo. Todas las hojas actuales de este set serán reemplazadas. Esta acción no se puede deshacer automáticamente. ¿Deseas continuar?'
                    : 'You are about to replace this plan set with the new file. All current sheets in this set will be replaced. This action cannot be automatically undone. Do you want to continue?'}
                </p>
                <div className="text-[11px] font-[600] text-[#6B6660] bg-[#FAFAF8] border border-[#ECEAE4] rounded-lg px-3 py-2">
                  <div>{isEs ? 'Set seleccionado' : 'Selected set'}: <span className="text-[#1C1A17] font-[700]">{planSets.find(s => s.id === replaceTargetSetId)?.name || '—'}</span></div>
                  <div>{isEs ? 'Nuevo archivo' : 'New file'}: <span className="text-[#1C1A17] font-[700]">{pendingBatch[0]?.file.name || '—'}{pendingBatch.length > 1 ? ` (+${pendingBatch.length - 1})` : ''}</span></div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button data-testid="plan-replace-warn-cancel" onClick={cancelBatch} className="text-[12px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg min-h-[44px] px-3 py-2">{isEs ? 'Cancelar' : 'Cancel'}</button>
                  <button data-testid="plan-replace-warn-confirm" onClick={() => runBatchUpload('replace', replaceTargetSetId)} className="text-[12px] font-[700] text-white bg-red-600 hover:bg-red-700 rounded-lg min-h-[44px] px-3 py-2">{isEs ? 'Sí, reemplazar set' : 'Yes, replace set'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Plan-set lifecycle confirmation (PR-PM5) — archive / restore / supersede. */}
      {pendingSetAction && (
        <div data-testid="plan-set-action-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-sm sm:mx-4 p-5 space-y-4 max-h-[80vh] overflow-y-auto" style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
            <div className="text-sm font-[800] text-gray-900">
              {pendingSetAction.action === 'archive' ? (isEs ? 'Archivar set' : 'Archive set')
                : pendingSetAction.action === 'restore' ? (isEs ? 'Restaurar set como actual' : 'Restore set as current')
                : (isEs ? 'Marcar set como reemplazado' : 'Mark set as superseded')}
            </div>
            <p className="text-[12px] text-gray-700">
              {pendingSetAction.action === 'archive'
                ? (isEs ? `Archivar "${pendingSetAction.setName}". Quedará en solo lectura y podrás restaurarlo. ¿Continuar?` : `Archive "${pendingSetAction.setName}". It becomes read-only and can be restored later. Continue?`)
                : pendingSetAction.action === 'restore'
                  ? (isEs ? `Restaurar "${pendingSetAction.setName}" como el set actual (editable). ¿Continuar?` : `Restore "${pendingSetAction.setName}" as the current (editable) set. Continue?`)
                  : (isEs ? `Marcar "${pendingSetAction.setName}" como reemplazado. Quedará en solo lectura. ¿Continuar?` : `Mark "${pendingSetAction.setName}" as superseded. It becomes read-only. Continue?`)}
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button data-testid="plan-set-action-cancel" onClick={() => setPendingSetAction(null)} className="text-[12px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-2">{isEs ? 'Cancelar' : 'Cancel'}</button>
              <button data-testid="plan-set-action-confirm" onClick={confirmSetAction} className={`text-[12px] font-[700] text-white rounded-lg px-3 py-2 ${pendingSetAction.action === 'restore' ? 'bg-[#06539B] hover:bg-[#063e73]' : 'bg-amber-600 hover:bg-amber-700'}`}>{isEs ? 'Confirmar' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {/* PR-PM7: delete-layer confirmation (DESKTOP). Never deletes markups silently
          — shows the count and, when there are markups, forces an explicit choice
          between moving them or deleting them. On TOUCH this is replaced by the
          full-screen mobile action sheet below (gated !isCoarsePointer here). */}
      {pendingLayerDelete && !isCoarsePointer && (
        <div data-testid="layer-delete-modal" className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-sm sm:mx-4 p-5 space-y-4 max-h-[80vh] overflow-y-auto" style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
            <h3 className="text-[15px] font-[900] text-[#1C1A17]">{isEs ? 'Borrar capa' : 'Delete layer'}</h3>
            {renderLayerDeleteBody()}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <button data-testid="layer-delete-cancel" onClick={() => setPendingLayerDelete(null)} className="text-[12px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-2">{isEs ? 'Cancelar' : 'Cancel'}</button>
              <button data-testid="layer-delete-confirm" onClick={confirmDeleteLayer} className={`text-[12px] font-[700] text-white rounded-lg px-3 py-2 ${layerDeleteIsPurge() ? 'bg-red-600 hover:bg-red-700' : 'bg-[#06539B] hover:bg-[#063e73]'}`}>
                {layerDeleteConfirmText()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PR-PM10: MOBILE layer action sheet — create/rename/delete as a dedicated
          near-full-screen sheet so the iOS keyboard can never hide the controls and
          the user can't get trapped (only ONE action is ever active — enforced by
          resetLayerActionState). RENAME keeps its Save/Cancel in the STICKY HEADER
          (top of screen) because the keyboard only covers the bottom; DELETE (no
          keyboard) keeps Confirm/Cancel in the sticky footer. Touch-only (md:hidden +
          isCoarsePointer); desktop uses the inline rename + centered modal above. */}
      {isCoarsePointer && (renamingLayerId || pendingLayerDelete) && (
        <div data-testid="mobile-layer-action-sheet" className="fixed inset-0 z-[60] flex flex-col bg-white md:hidden">
          <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 border-b border-[#ECEAE4] bg-white" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
            <span className="text-[15px] font-[900] text-[#1C1A17] flex items-center gap-1.5 flex-1 min-w-0">
              {renamingLayerId ? <Pencil size={15} className="flex-shrink-0" /> : <Trash2 size={15} className="flex-shrink-0" />}
              <span className="truncate">{renamingLayerId ? (isEs ? 'Nombre de la capa' : 'Layer name') : (isEs ? 'Borrar capa' : 'Delete layer')}</span>
            </span>
            {renamingLayerId ? (
              <>
                <button data-testid="mobile-layer-action-cancel" onClick={cancelRenameLayer} className="text-[13px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-2">{isEs ? 'Cancelar' : 'Cancel'}</button>
                <button data-testid="mobile-layer-action-confirm" onClick={commitRenameLayer} className="text-[13px] font-[800] text-white bg-[#06539B] hover:bg-[#063e73] rounded-lg px-4 py-2">{isEs ? 'Guardar' : 'Save'}</button>
              </>
            ) : (
              <button data-testid="mobile-layer-action-close" onClick={closeLayerAction} title={isEs ? 'Cerrar' : 'Close'} className="p-2 -mr-2 rounded-lg text-[#9E9890] hover:text-[#1C1A17] hover:bg-[#F2F0EB]">
                <X size={20} />
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {renamingLayerId ? (
              <div className="space-y-2">
                <label className="block text-[12px] font-[700] text-[#6B6660]">{isEs ? 'Nombre de la capa' : 'Layer name'}</label>
                {/* text-[16px] prevents iOS Safari from auto-zooming the page on focus. */}
                <input
                  data-testid="mobile-layer-rename-input"
                  autoFocus
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') commitRenameLayer(); if (e.key === 'Escape') cancelRenameLayer() }}
                  className="block w-full text-[16px] font-[700] text-[#1C1A17] border border-[#06539B] rounded-lg px-3 py-3 outline-none"
                />
                <p className="text-[11px] font-[600] text-[#9E9890]">{isEs ? 'Toca Guardar arriba cuando termines.' : 'Tap Save above when done.'}</p>
              </div>
            ) : pendingLayerDelete ? (
              renderLayerDeleteBody()
            ) : null}
          </div>

          {/* DELETE footer (no keyboard) — Confirm/Cancel pinned with safe-area pad. */}
          {pendingLayerDelete && !renamingLayerId && (
            <div className="sticky bottom-0 z-10 flex flex-col-reverse gap-2 px-4 pt-3 border-t border-[#ECEAE4] bg-white" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
              <button data-testid="mobile-layer-action-cancel" onClick={() => setPendingLayerDelete(null)} className="w-full text-[13px] font-[700] text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-3">{isEs ? 'Cancelar' : 'Cancel'}</button>
              <button data-testid="mobile-layer-action-confirm" onClick={confirmDeleteLayer} className={`w-full text-[13px] font-[800] text-white rounded-lg px-3 py-3 ${layerDeleteIsPurge() ? 'bg-red-600 hover:bg-red-700' : 'bg-[#06539B] hover:bg-[#063e73]'}`}>{layerDeleteConfirmText()}</button>
            </div>
          )}
        </div>
      )}

      {/* KPIs — stack on phones, 3-up from sm. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('drawing.kpi.annotations')}</div>
          <div className="text-[18px] font-[900] text-[#06539B]">{totalMarkups}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('drawing.kpi.layers')}</div>
          <div className="text-[18px] font-[900] text-[#1C1A17]">{layerCount}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('drawing.kpi.visible')}</div>
          <div className="text-[18px] font-[900] text-[#22C55E]">{visibleCount}</div>
        </div>
      </div>

      {/* Sheet navigation
          Hidden entirely when no real sheet exists. Rendering the
          previous "1/0" / "0/0" counter advertised a broken state and
          let the user click a phantom sheet button. The empty-state
          UI inside the canvas (below) is the single source of truth
          for the "no drawings uploaded" affordance. */}
      {/* Plan-set / revision list (PR-PM3). Each uploaded file/revision is its own
          set; the user sees them as separate sets and selects the active one. */}
      {planSets.length > 0 && (
        <div data-testid="plan-set-list" className="bg-white border border-[#ECEAE4] rounded-xl p-3 mb-2">
          <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-2">
            {isEs ? 'Sets de planos / Revisiones' : 'Plan sets / Revisions'}
          </div>
          <div className="flex flex-col gap-1.5">
            {planSets.map(ps => {
              const isActive = ps.id === effectiveSetId
              const isArchived = ps.status === 'ARCHIVED'
              const isSuperseded = ps.status === 'SUPERSEDED'
              return (
                <div
                  key={ps.id}
                  data-testid="plan-set-item"
                  className={`rounded-lg px-3 py-2 border transition-colors ${isActive ? 'border-[#06539B] bg-[#06539B]/5' : 'border-[#ECEAE4]'}`}
                >
                  <button
                    onClick={() => { setActiveSetId(ps.id); setActiveSheetIdx(0) }}
                    className="block w-full text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-[800] text-[#1C1A17] truncate flex-1">{ps.name}</span>
                      {isActive && <span data-testid="plan-set-current-badge" className="text-[8px] font-[800] uppercase text-white bg-[#06539B] rounded px-1.5 py-0.5">{isEs ? 'Actual' : 'Current'}</span>}
                      <span data-testid="plan-set-status-badge" className={`text-[8px] font-[800] uppercase rounded px-1.5 py-0.5 ${isArchived ? 'bg-gray-200 text-gray-600' : isSuperseded ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {isArchived ? (isEs ? 'Archivado' : 'Archived') : isSuperseded ? (isEs ? 'Reemplazado' : 'Superseded') : (isEs ? 'Actual' : 'Current')}
                      </span>
                    </div>
                    <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
                      {ps.sourceFile || '—'}
                      {ps.revisionLabel ? ` · ${ps.revisionLabel}` : ''}
                      {' · '}{ps.sheetCount ?? 0} {isEs ? 'hojas' : 'sheets'}
                      {ps.createdAt ? ` · ${new Date(ps.createdAt).toLocaleDateString(isEs ? 'es' : 'en')}` : ''}
                      {ps.uploadedByLabel ? ` · ${isEs ? 'Subido por' : 'Uploaded by'}: ${actorLabelText(ps.uploadedByLabel, isEs)}` : ''}
                      {(isArchived || isSuperseded) ? ` · ${isEs ? 'Solo lectura' : 'Read-only'}` : ''}
                    </div>
                  </button>
                  {/* Lifecycle actions (PR-PM5) — each confirms before changing status. */}
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {/* PR-PM6 fix: when this is the ONLY current set, archiving/
                        superseding is not allowed — so we don't render an actionable
                        button at all (the click-time guard + backend NO_CURRENT_LEFT
                        stay as defense-in-depth). A small info label explains why. */}
                    {ps.status === 'CURRENT' && (
                      isOnlyCurrentSet(ps) ? (
                        <span data-testid="plan-set-archive-unavailable" className="text-[10px] font-[600] italic text-[#9E9890]">
                          {isEs ? 'Archivar no está disponible mientras este sea el único set actual.' : 'Archive unavailable while this is the only current set.'}
                        </span>
                      ) : (
                        <>
                          <button data-testid="plan-set-archive" disabled={settingSetStatus} onClick={() => requestSetAction(ps, 'archive')} className="text-[10px] font-[700] text-gray-600 hover:bg-gray-100 border border-[#ECEAE4] rounded px-2 py-0.5 disabled:opacity-40">{isEs ? 'Archivar' : 'Archive'}</button>
                          <button data-testid="plan-set-supersede" disabled={settingSetStatus} onClick={() => requestSetAction(ps, 'supersede')} className="text-[10px] font-[700] text-amber-700 hover:bg-amber-50 border border-amber-200 rounded px-2 py-0.5 disabled:opacity-40">{isEs ? 'Marcar reemplazado' : 'Mark superseded'}</button>
                        </>
                      )
                    )}
                    {(isArchived || isSuperseded) && (
                      <button data-testid="plan-set-restore" disabled={settingSetStatus} onClick={() => requestSetAction(ps, 'restore')} className="text-[10px] font-[700] text-[#06539B] hover:bg-[#06539B]/5 border border-[#06539B]/30 rounded px-2 py-0.5 disabled:opacity-40">{isEs ? 'Restaurar como actual' : 'Restore as current'}</button>
                    )}
                    {isSuperseded && (
                      <button data-testid="plan-set-archive" disabled={settingSetStatus} onClick={() => requestSetAction(ps, 'archive')} className="text-[10px] font-[700] text-gray-600 hover:bg-gray-100 border border-[#ECEAE4] rounded px-2 py-0.5 disabled:opacity-40">{isEs ? 'Archivar' : 'Archive'}</button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {activeSetReadOnly && (
        <div data-testid="plan-set-readonly-banner" className="text-[11px] font-[700] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
          {activeSet?.status === 'ARCHIVED'
            ? (isEs ? 'Este set está archivado (solo lectura). Restáuralo para editar marcas.' : 'This set is archived (read-only). Restore it to edit markups.')
            : (isEs ? 'Este set está reemplazado (solo lectura). Restáuralo para editar marcas.' : 'This set is superseded (read-only). Restore it to edit markups.')}
        </div>
      )}

      {hasRealActiveSheet && (
        <div data-testid="plan-file-info" className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-white border border-[#ECEAE4] rounded-xl px-3 py-2 mb-2 text-[10px] font-[700] text-[#6B6660]">
          <span>{isEs ? 'Set' : 'Set'}: <span className="text-[#1C1A17]">{activeSet?.name || '—'}</span></span>
          <span>{isEs ? 'Archivo' : 'File'}: <span className="text-[#1C1A17]">{activeSheetRef?.sourceFile || '—'}</span></span>
          {activeSheetRef?.uploadedAt && (
            <span>{isEs ? 'Subido' : 'Uploaded'}: <span className="text-[#1C1A17]">{new Date(activeSheetRef.uploadedAt).toLocaleString(isEs ? 'es' : 'en')}</span></span>
          )}
          <span>{isEs ? 'Hojas' : 'Sheets'}: <span className="text-[#1C1A17]">{activeSheets.length}</span></span>
        </div>
      )}

      {hasRealActiveSheet ? (
        <div className="flex items-center gap-2 bg-white border border-[#ECEAE4] rounded-xl px-3 py-2">
          <button
            onClick={() => setActiveSheetIdx(Math.max(0, activeSheetIdx - 1))}
            disabled={activeSheetIdx === 0}
            className="p-1 rounded hover:bg-[#F2F0EB] disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={14} className="text-[#9E9890]" />
          </button>
          <div className="flex gap-1 flex-1 overflow-x-auto">
            {activeSheets.map((sheet, idx) => {
              const count = elements.filter(el => el.sheetId === sheet.id).length
              return (
                <button
                  key={sheet.id}
                  onClick={() => setActiveSheetIdx(idx)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-[700] whitespace-nowrap transition-colors ${
                    idx === activeSheetIdx
                      ? 'bg-[#06539B] text-white'
                      : 'text-[#9E9890] hover:bg-[#F2F0EB] hover:text-[#1C1A17]'
                  }`}
                >
                  <FileText size={11} />
                  {isEs ? sheet.nameEs : sheet.name}
                  {count > 0 && <span className="text-[8px] font-[800] opacity-70">({count})</span>}
                </button>
              )
            })}
          </div>
          <button
            onClick={() => setActiveSheetIdx(Math.min(activeSheets.length - 1, activeSheetIdx + 1))}
            disabled={activeSheetIdx === activeSheets.length - 1}
            className="p-1 rounded hover:bg-[#F2F0EB] disabled:opacity-30 transition-colors"
          >
            <ChevronRight size={14} className="text-[#9E9890]" />
          </button>
          <span className="text-[9px] font-[700] text-[#9E9890] ml-1">
            {activeSheetIdx + 1}/{activeSheets.length}
          </span>
        </div>
      ) : null}

      <div className="flex flex-col md:flex-row gap-4">
        {/* Main canvas area */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Toolbar */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl px-3 py-2 flex items-center gap-1 flex-wrap">
            {TOOLS.map(tool => {
              const toolLabel = t(`drawing.tools.${tool.type}`)
              // Empty-state safety: drawing tools (pen/line/arrow/
              // rectangle/circle/text) are disabled when no real sheet
              // exists. We keep "select" enabled so a user can still
              // click on legacy elements (in case any survive a
              // hydration race). The mouse handlers themselves are
              // also gated — this is the visible affordance.
              const isDrawingTool = tool.type !== 'select'
              const disabled = isDrawingTool && !hasRealActiveSheet
              // PR-PM7: also block drawing tools when the active layer is locked.
              const lockedOut = isDrawingTool && activeLayerLocked
              // PR-PM7 (mobile fix): touch drawing isn't implemented → grey out the
              // markup tools on coarse-pointer devices so they don't look usable.
              // `select` (a non-drawing tool) stays enabled for viewing.
              const touchDisabled = isDrawingTool && isCoarsePointer
              return (
                <button
                  key={tool.type}
                  onClick={() => { setActiveTool(tool.type); setSelectedId(null) }}
                  disabled={disabled || lockedOut || touchDisabled}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-[700] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    activeTool === tool.type
                      ? 'bg-[#06539B] text-white'
                      : 'text-[#9E9890] hover:bg-[#F2F0EB] hover:text-[#1C1A17]'
                  }`}
                  title={touchDisabled
                    ? (isEs ? 'Disponible en computadora' : 'Available on desktop')
                    : lockedOut
                    ? (isEs ? 'La capa activa está bloqueada' : 'The active layer is locked')
                    : disabled
                    ? (isEs
                      ? 'Sube planos para usar esta herramienta'
                      : 'Upload plans to use this tool')
                    : toolLabel}
                >
                  <tool.icon size={13} />
                  <span className="hidden sm:inline">{toolLabel}</span>
                </button>
              )
            })}

            <div className="w-px h-6 bg-[#ECEAE4] mx-1" />

            {/* Color picker */}
            <div className="relative">
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[12px] font-[700] text-[#9E9890] hover:bg-[#F2F0EB]"
              >
                <div className="w-4 h-4 rounded-full border border-[#ECEAE4]" style={{ background: activeColor }} />
                <Palette size={13} />
              </button>
              {showColorPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-[#ECEAE4] rounded-xl p-2 shadow-lg z-20 flex gap-1">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => { setActiveColor(c); setShowColorPicker(false) }}
                      className={`w-6 h-6 rounded-full border-2 transition-transform ${activeColor === c ? 'border-[#1C1A17] scale-110' : 'border-transparent'}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Stroke width */}
            <div className="flex items-center gap-1 ml-1">
              {STROKE_WIDTHS.map(w => (
                <button
                  key={w}
                  onClick={() => setActiveStroke(w)}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                    activeStroke === w ? 'bg-[#06539B]' : 'hover:bg-[#F2F0EB]'
                  }`}
                >
                  <div
                    className="rounded-full"
                    style={{ width: w + 4, height: w + 4, background: activeStroke === w ? 'white' : '#9E9890' }}
                  />
                </button>
              ))}
            </div>

            <div className="w-px h-6 bg-[#ECEAE4] mx-1" />

            {/* Delete selected */}
            {selectedId && (
              <button
                onClick={deleteSelected}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-[700] text-[#EF4444] hover:bg-red-50"
              >
                <Trash2 size={13} /> {t('drawing.delete')}
              </button>
            )}

            {/* PR-PM7 (mobile fix): Layers toggle lives WITH the markup tools (not the
                page header) since layers are a markup concern. Opens the side panel
                (desktop) / bottom sheet (mobile). Pushed to the right on wide rows. */}
            <button
              data-testid="layers-toggle"
              onClick={() => setShowLayers(v => !v)}
              aria-pressed={showLayers}
              title={isEs ? 'Capas' : 'Layers'}
              className={`sm:ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-[700] transition-colors ${showLayers ? 'bg-[#06539B] text-white' : 'text-[#9E9890] hover:bg-[#F2F0EB] hover:text-[#1C1A17]'}`}
            >
              <Layers size={13} /> {isEs ? 'Capas' : 'Layers'}
            </button>
          </div>

          {/* PR-PM6/PM7: honest touch note. Drawing markups needs a mouse; on touch
              devices the markup tools are greyed out (above) and we say so plainly
              rather than letting drawing silently fail. Viewing, navigation, upload,
              export, sets and layers all work on touch. Shown whenever on a coarse
              pointer (not only when a sheet is open) so the disabled tools are
              explained. */}
          {isCoarsePointer && (
            <div data-testid="touch-drawing-note" className="text-[11px] font-[600] text-[#9E6B00] bg-[#FEF7E6] border border-[#F5E2B8] rounded-lg px-3 py-2">
              {isEs
                ? 'Las herramientas de marcación están disponibles en computadora. La vista móvil está optimizada para revisar planos, administrar sets, capas y exportar.'
                : 'Markup drawing tools are available on desktop. Mobile view is optimized for reviewing plans, managing sets, layers, and exports.'}
            </div>
          )}

          {/* PR-PM7: clear disabled state when the active layer is locked — drawing
              and editing are blocked; the user can unlock or switch layers. */}
          {activeLayerLocked && hasRealActiveSheet && activeSetEditable && (
            <div data-testid="layer-locked-banner" className="text-[11px] font-[700] text-[#9E6B00] bg-[#FEF7E6] border border-[#F5E2B8] rounded-lg px-3 py-2 flex items-center gap-1.5">
              <Lock size={12} />
              {isEs
                ? 'La capa activa está bloqueada. Desbloquéala o cambia de capa para dibujar o editar.'
                : 'The active layer is locked. Unlock it or switch layers to draw or edit.'}
            </div>
          )}

          {/* SVG Canvas
              Cursor reflects interactivity: crosshair only when a
              real sheet is loaded, the active tool is a drawing tool,
              AND the active layer is unlocked (canDraw). Otherwise the
              canvas is inert. */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
            <svg
              ref={svgRef}
              width="100%"
              height="420"
              viewBox="0 0 700 420"
              className={`bg-[#FAFAF8] ${
                canDraw && activeTool !== 'select'
                  ? 'cursor-crosshair'
                  : 'cursor-default'
              }`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onClick={() => { if (activeTool === 'select') setSelectedId(null) }}
            >
              {/* Grid */}
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ECEAE4" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />

              {/* Plan background — rendered page image, empty
                  state for real projects without uploaded
                  drawings, or a sheet placeholder (demo path /
                  hydration in progress). */}
              {activeSheetRef?.imageUrl && !failedSheets.has(activeSheetRef.id) ? (
                <image
                  key={`${activeSheetRef.id}:${imageReloadNonce}`}
                  href={sheetImageHref}
                  x="0" y="0" width="700" height="420"
                  preserveAspectRatio="xMidYMid meet"
                  onLoad={() => handleSheetImageLoad(activeSheetRef.id)}
                  onError={() => handleSheetImageError(activeSheetRef.id)}
                />
              ) : !hasRealActiveSheet ? (
                // PR-NEW: honest empty state for real projects
                // with no uploaded drawings yet. Replaces the old
                // hidden DEMO_SHEETS that used to render fake
                // "A1 — Floor Plan" / "A2 — Elevations" sheets.
                <>
                  <rect x="40" y="30" width="620" height="360" fill="#FAFAF8" stroke="#ECEAE4" strokeWidth="1" strokeDasharray="8 4" rx="4" />
                  <text x="350" y="180" textAnchor="middle" fill="#9E9890" fontSize="14" fontWeight="800" fontFamily="sans-serif">
                    {isEs ? 'Aún no hay planos subidos' : 'No drawings uploaded yet'}
                  </text>
                  <text x="350" y="208" textAnchor="middle" fill="#9E9890" fontSize="11" fontWeight="600" fontFamily="sans-serif">
                    {isEs
                      ? 'Sube planos para empezar a marcar hojas'
                      : 'Upload plans to start marking up sheets'}
                  </text>
                  <text x="350" y="226" textAnchor="middle" fill="#9E9890" fontSize="11" fontWeight="600" fontFamily="sans-serif">
                    {isEs ? 'para comenzar a marcar hojas.' : 'to start marking up sheets.'}
                  </text>
                </>
              ) : activeSheetRef ? (
                // A real sheet whose image is missing or failed to load — show a
                // clear, specific render-error state, never a fake placeholder.
                <>
                  <rect x="40" y="30" width="620" height="360" fill="#FFF7F6" stroke="#F2C9C4" strokeWidth="1" strokeDasharray="8 4" rx="4" />
                  <text x="350" y="200" textAnchor="middle" fill="#B4534B" fontSize="13" fontWeight="800" fontFamily="sans-serif">
                    {isEs ? 'Esta hoja no se pudo renderizar.' : 'This sheet could not be rendered.'}
                  </text>
                  <text x="350" y="224" textAnchor="middle" fill="#C77A72" fontSize="11" fontWeight="600" fontFamily="sans-serif">
                    {!activeSheetRef.imageUrl
                      ? (isEs ? 'Falta la imagen de la hoja.' : 'Sheet image is missing.')
                      : (isEs ? 'No se pudo cargar la imagen de la hoja.' : 'Sheet image could not be loaded.')}
                  </text>
                  {activeSheetRef.imageUrl && (
                    <text x="350" y="244" textAnchor="middle" fill="#C77A72" fontSize="11" fontWeight="600" fontFamily="sans-serif">
                      {isEs ? 'Intenta subirla nuevamente.' : 'Try uploading again.'}
                    </text>
                  )}
                </>
              ) : null}

              {/* Rendered elements */}
              {visibleElements.map(renderElement)}

              {/* Live pen preview */}
              {isDrawing && activeTool === 'pen' && penPoints.length > 1 && (
                <polyline
                  points={penPoints.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none" stroke={activeColor} strokeWidth={activeStroke}
                  strokeLinecap="round" strokeLinejoin="round" opacity={0.6}
                />
              )}
            </svg>
          </div>
        </div>

        {/* Layers panel.
            - Desktop (md+): fixed-width side panel next to the canvas (unchanged).
            - Mobile (<md): a bottom sheet (PR-PM7 fix) — fixed to the bottom of the
              viewport, scrollable, with safe-area padding so its actions are never
              hidden behind the Safari bottom bar. A backdrop + close button let the
              user dismiss it. */}
        {showLayers && (
          <>
            {/* Mobile-only dimmed backdrop to dismiss the bottom sheet. */}
            <div className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setShowLayers(false)} />
          <div
            data-testid="layers-panel"
            className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-2xl shadow-2xl pb-[calc(env(safe-area-inset-bottom)+4rem)] md:static md:inset-auto md:bottom-auto md:z-auto md:max-h-none md:overflow-visible md:rounded-xl md:shadow-none md:pb-0 w-full md:w-[200px] flex-shrink-0 bg-white border border-[#ECEAE4] self-start"
          >
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#ECEAE4] sticky top-0 bg-white z-10">
              <span className="text-[11px] font-[800] text-[#1C1A17] flex items-center gap-1.5">
                <Layers size={13} /> {isEs ? 'Capas' : 'Layers'}
              </span>
              <div className="flex items-center gap-1">
              {/* Mobile-only close (X) for the bottom sheet. */}
              <button
                data-testid="layers-close"
                onClick={() => setShowLayers(false)}
                title={isEs ? 'Cerrar' : 'Close'}
                className="p-1.5 rounded hover:bg-[#F2F0EB] text-[#9E9890] hover:text-[#1C1A17] md:hidden"
              >
                <X size={14} />
              </button>
              {/* Add layer — disabled (not hidden) on read-only sets with a clear tip. */}
              <button
                data-testid="layer-add"
                onClick={addLayer}
                disabled={!layersEditable}
                title={!isOwnerEditable
                  ? noEditPermMsg
                  : !activeSetEditable
                  ? (isEs ? 'Set de solo lectura' : 'Read-only set')
                  : (isEs ? 'Agregar capa' : 'Add layer')}
                className="p-1 rounded hover:bg-[#F2F0EB] text-[#9E9890] hover:text-[#1C1A17] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={13} />
              </button>
              </div>
            </div>

            {/* Read-only set note: layer editing is disabled (view/visibility only). */}
            {!layersEditable && (
              <div data-testid="layers-readonly-note" className="px-3 py-2 text-[9px] font-[700] text-[#9E6B00] bg-[#FEF7E6] border-b border-[#F5E2B8]">
                {isEs ? 'Set de solo lectura — edición de capas deshabilitada.' : 'Read-only set — layer editing disabled.'}
              </div>
            )}

            {layerError && (
              <div data-testid="layer-error" className="px-3 py-2 text-[9px] font-[700] text-red-600 bg-red-50 border-b border-red-100">
                {layerError}
              </div>
            )}

            <div className="divide-y divide-[#F2F0EB]">
              {layers.map(layer => {
                const isRenaming = renamingLayerId === layer.id
                // PR-PM10: on touch the rename happens in the full-screen action
                // sheet (keyboard-safe), so the cramped inline input/Save/Cancel are
                // DESKTOP-only. On mobile the row keeps showing the name + actions.
                const isInlineRenaming = isRenaming && !isCoarsePointer
                return (
                <div
                  key={layer.id}
                  data-testid="layer-item"
                  className={`flex items-center gap-1.5 px-3 py-2 transition-colors ${activeLayerId === layer.id ? 'bg-[#F2F0EB]' : 'hover:bg-[#FAFAF8]'} ${isInlineRenaming ? '' : 'cursor-pointer'}`}
                  onClick={() => { if (!isInlineRenaming) setActiveLayerId(layer.id) }}
                >
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: layer.color }} />
                  {isInlineRenaming ? (
                    <input
                      data-testid="layer-rename-input"
                      autoFocus
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => { if (e.key === 'Enter') commitRenameLayer(); if (e.key === 'Escape') cancelRenameLayer() }}
                      className="flex-1 min-w-0 text-[10px] font-[700] text-[#1C1A17] border border-[#06539B] rounded px-1.5 py-0.5 outline-none"
                    />
                  ) : (
                    <span className="text-[10px] font-[700] text-[#1C1A17] flex-1 truncate flex items-center gap-1">
                      {layer.locked && <Lock size={9} className="text-[#9E6B00] flex-shrink-0" />}
                      {localizeLayerName(layer)}
                    </span>
                  )}

                  {isInlineRenaming ? (
                    <>
                      <button data-testid="layer-rename-save" onClick={e => { e.stopPropagation(); commitRenameLayer() }} className="p-1.5 md:p-0.5 text-emerald-600 hover:text-emerald-700"><Check size={12} /></button>
                      <button data-testid="layer-rename-cancel" onClick={e => { e.stopPropagation(); cancelRenameLayer() }} className="p-0.5 text-[#9E9890] hover:text-[#1C1A17]"><X size={12} /></button>
                    </>
                  ) : (
                    <>
                      {/* Show/hide — always allowed (view/export concern). */}
                      <button
                        data-testid="layer-visibility"
                        onClick={(e) => { e.stopPropagation(); toggleLayer(layer.id) }}
                        title={layer.visible ? (isEs ? 'Ocultar capa' : 'Hide layer') : (isEs ? 'Mostrar capa' : 'Show layer')}
                        className="p-1.5 md:p-0.5 text-[#9E9890] hover:text-[#1C1A17]"
                      >
                        {layer.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                      </button>
                      {/* Lock / Rename / Delete — only on editable sets. */}
                      {layersEditable && (
                        <>
                          <button
                            data-testid="layer-lock"
                            onClick={(e) => { e.stopPropagation(); toggleLockLayer(layer.id) }}
                            title={layer.locked ? (isEs ? 'Desbloquear capa' : 'Unlock layer') : (isEs ? 'Bloquear capa' : 'Lock layer')}
                            className="p-1.5 md:p-0.5 text-[#9E9890] hover:text-[#1C1A17]"
                          >
                            {layer.locked ? <Lock size={12} /> : <Unlock size={12} />}
                          </button>
                          <button
                            data-testid="layer-rename"
                            onClick={(e) => { e.stopPropagation(); startRenameLayer(layer) }}
                            title={isEs ? 'Renombrar capa' : 'Rename layer'}
                            className="p-1.5 md:p-0.5 text-[#9E9890] hover:text-[#1C1A17]"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            data-testid="layer-delete"
                            onClick={(e) => { e.stopPropagation(); requestDeleteLayer(layer) }}
                            disabled={layers.length <= 1}
                            title={layers.length <= 1
                              ? (isEs ? 'No puedes borrar la última capa' : 'You can’t delete the last layer')
                              : (isEs ? 'Borrar capa' : 'Delete layer')}
                            className="p-1.5 md:p-0.5 text-[#9E9890] hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[#9E9890]"
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
                )
              })}
            </div>
            <div className="px-3 py-2 text-[9px] font-[600] text-[#9E9890]">
              {hasRealActiveSheet && activeSheetRef
                ? elements.filter(el => el.layerId === activeLayerId && el.sheetId === activeSheetRef.id).length
                : 0} {isEs ? 'elementos en capa activa' : 'elements in active layer'}
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  )
}
